/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr, passwordPolicyError } from "@/lib/utils";
import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from "react";
import { enqueue, coalesceUpdate, dropPending } from '../lib/offlineQueue';
import { isOffline, subscribe as subscribeConnettivita } from '../lib/connectivity';
import { fmtCognome, fmtNome, useSelectOptionsGuard } from "./shared";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
// Export PDF on-demand: jspdf + jspdf-autotable via dynamic import
// (chunk separato, fuori dal bundle principale → first-load più rapido).
const loadPdf = async () => {
  const [{ jsPDF }, at] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  return { jsPDF, autoTable: at.default };
};
import { QRCodeSVG } from "qrcode.react";
import {
  LayoutDashboard, Users, UsersRound, MapPin, ClipboardList, Plus, Search,
  AlertTriangle, X, Edit2, Trash2, Radio, Clock, Target, Shield, FileDown,
  Printer, Upload, MessageSquare, Phone, Megaphone, Send, LogOut, UserCheck,
  UserX, QrCode, Eye, EyeOff, History, CheckCircle, XCircle, LogIn, FileText,
  Mail, RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

// ─────────────────────────────────────────────────────────────────────────────
//  Feature flag POA (Probability of Area)
// ─────────────────────────────────────────────────────────────────────────────
// Quando false (REACT_APP_POA_ENABLED=false su Railway), tutta la UI POA
// e' nascosta: sidebar, scheda disperso, cronologico indizi, admin
// validazione POA, modale POD, ecc. Il backend ha la sua propria env var
// POA_ENABLED che fa rispondere 404 sui rispettivi endpoint.
//
// Default: true (POA attivo). Imposta REACT_APP_POA_ENABLED=false su
// Railway frontend e rebuilda per disattivare temporaneamente.
//
// NOTA: REACT_APP_* variabili sono incorporate nel bundle al build time
// (Create React App), quindi cambiarle richiede un rebuild del frontend
// (Railway lo fa automaticamente al cambio variabile, ~2-4 min).
const POA_ENABLED = (process.env.REACT_APP_POA_ENABLED || 'true').toLowerCase() === 'true';

/**
 * Restituisce data e ora locali del browser nel fuso orario del sistema operativo.
 * Gestisce automaticamente il cambio ora solare/legale (es. CEST UTC+2 in estate,
 * CET UTC+1 in inverno) senza bisogno di librerie esterne.
 */
const localNow = () => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  return {
    data: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    ora: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with auth
// Header dell'operatore "in turno" (cambio turno su PCA condiviso): annota
// l'azione nell'audit senza toccare l'autenticazione (resta il JWT). Chi tiene lo
// stato passa il valore esplicito a createApi; il fallback su localStorage serve
// ai punti che costruiscono l'api al volo (login, cambio turno).
const actingOperatorHeader = (override) => {
  // `override` esplicito (passato da chi tiene lo stato) batte localStorage: lo
  // storage viene scritto da un effect, quindi nel render subito dopo un cambio
  // turno conterrebbe ancora il valore vecchio.
  if (override !== undefined) {
    return override ? { "X-Acting-Operator": encodeURIComponent(override) } : {};
  }
  try {
    const v = localStorage.getItem("tas_acting_operator");
    return v ? { "X-Acting-Operator": encodeURIComponent(v) } : {};
  } catch { return {}; }
};

/**
 * Chiave di idempotenza per una CREAZIONE: generata una volta e rispedita identica a
 * ogni tentativo, così una POST rigiocata dopo una caduta di rete non crea un
 * doppione. Un errore di rete non dice se il server avesse già scritto — la risposta
 * può essersi persa al ritorno — quindi senza chiave il replay duplica.
 * `crypto.randomUUID` non c'è su contesti non sicuri o browser vecchi: fallback.
 */
const idemKey = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* contesto non sicuro */ }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const createApi = (token, actingOverride) => {
  const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...actingOperatorHeader(actingOverride) };
  return {
    // Auth
    register: (data) => axios.post(`${API}/auth/register`, data),
    login: (data) => axios.post(`${API}/auth/login`, data),
    resetPassword: (data) => axios.post(`${API}/auth/reset-password`, data),
    resetPasswordConfirm: (data) => axios.post(`${API}/auth/reset-password/confirm`, data),
    verifyEmail: (token) => axios.get(`${API}/auth/verify-email`, { params: { token } }),
    resendVerification: (data) => axios.post(`${API}/auth/resend-verification`, data),
    getMe: () => axios.get(`${API}/auth/me`, { headers }),
    
    // Operazioni
    getOperazioni: () => axios.get(`${API}/operazioni`, { headers }),
    getOperazioneByToken: (token) => axios.get(`${API}/operazioni/by-token/${token}`),
    createOperazione: (data) => axios.post(`${API}/operazioni`, data, { headers }),
    accediConCodice: (codice) => axios.post(`${API}/operazioni/accedi`, { codice }, { headers }),
    getOperatoriNomi: () => axios.get(`${API}/operatori/nomi`, { headers }),
    getTas2Attivi: () => axios.get(`${API}/tas2-personale/attivi`, { headers }),
    updateOperazione: (id, data) => axios.put(`${API}/operazioni/${id}`, data, { headers }),
    patchOperazione: (id, data) => axios.patch(`${API}/operazioni/${id}`, data, { headers }),
    aggiungiPosizionePca: (id, data) => axios.post(`${API}/operazioni/${id}/pca`, data, { headers }),
    deleteOperazione: (id) => axios.delete(`${API}/operazioni/${id}`, { headers }),
    chiudiOperazione: (id) => axios.post(`${API}/operazioni/${id}/chiudi`, {}, { headers }),
    riapriOperazione: (id) => axios.post(`${API}/operazioni/${id}/riapri`, {}, { headers }),

    // Stato raccolta casi reali per la futura calibrazione locale del POA (admin).
    // Solo reportistica: il calcolo POA resta sui profili ISRID read-only.
    getStatoCalibrazionePoa: () => axios.get(`${API}/ritrovamenti-storici/stats/calibrazione-locale`, { headers }),

    // Soccorritori
    getSoccorritori: (operazioneId) => axios.get(`${API}/soccorritori`, { params: { operazione_id: operazioneId }, headers }),
    getPendingSoccorritori: (operazioneId) => axios.get(`${API}/soccorritori/pending`, { params: { operazione_id: operazioneId }, headers }),
    getSoccorritore: (id) => axios.get(`${API}/soccorritori/${id}`, { headers }),
    createSoccorritore: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createSoccorritore', { url: `${API}/soccorritori`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, stato: 'ATTIVO', _offline: true } };
      }
      return axios.post(`${API}/soccorritori`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    autoRegisterSoccorritore: (data) => { const { data: d, ora } = localNow(); return axios.post(`${API}/soccorritori/auto-register`, { ...data, data_arrivo: d, ora_arrivo: ora }); },
    // Tracking posizione soccorritori. getPosizioni = lato dashboard (auth operatore).
    // setTrackingConsenso / postPosizioni = lato device del soccorritore: usano il
    // tracking token (capability), NON il JWT operatore.
    getPosizioni: (operazioneId) => axios.get(`${API}/operazioni/${operazioneId}/posizioni`, { headers }),
    setTrackingConsenso: (soccId, trackingToken, attivo) => axios.post(`${API}/soccorritori/${soccId}/tracking-consenso`, { attivo }, { headers: { Authorization: `Bearer ${trackingToken}` } }),
    postPosizioni: (soccId, trackingToken, fixes) => axios.post(`${API}/soccorritori/${soccId}/posizioni`, { fixes }, { headers: { Authorization: `Bearer ${trackingToken}` } }),
    // SOS soccorritore (tracking token) + annulla (token) + clear lato operatore (JWT).
    // Stato della sessione dal telefono: dice se il soccorritore e' ancora in
    // elenco (404 se eliminato) e se e' stato convalidato.
    getTrackingStato: (soccId, trackingToken) => axios.get(`${API}/soccorritori/${soccId}/tracking-stato`, { headers: { Authorization: `Bearer ${trackingToken}` } }),
    sosSoccorritore: (soccId, trackingToken, fix) => axios.post(`${API}/soccorritori/${soccId}/sos`, fix, { headers: { Authorization: `Bearer ${trackingToken}` } }),
    annullaSos: (soccId, trackingToken) => axios.post(`${API}/soccorritori/${soccId}/sos/annulla`, {}, { headers: { Authorization: `Bearer ${trackingToken}` } }),
    clearSos: (soccId) => axios.post(`${API}/soccorritori/${soccId}/sos/clear`, {}, { headers }),
    updateSoccorritore: async (id, data) => {
      if (isOffline()) {
        // Creato offline e non ancora sincronizzato → fondi nella POST in coda
        if (String(id).startsWith('offline_')) {
          const merged = await coalesceUpdate(id, data);
          if (!merged) await enqueue('updateSoccorritore', { url: `${API}/soccorritori/${id}`, method: 'PUT', body: data });
        } else {
          await enqueue('updateSoccorritore', { url: `${API}/soccorritori/${id}`, method: 'PUT', body: data });
        }
        return { data: { ...data, id, _offline: true } };
      }
      return axios.put(`${API}/soccorritori/${id}`, data, { headers });
    },
    validateSoccorritore: async (id, action) => {
      if (isOffline()) {
        await enqueue('validateSoccorritore', { url: `${API}/soccorritori/${id}/validate`, method: 'POST', params: { action } });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/soccorritori/${id}/validate`, null, { params: { action }, headers });
    },
    markPartenza: async (id) => {
      const { data: d, ora } = localNow();
      if (isOffline()) {
        await enqueue('markPartenza', { url: `${API}/soccorritori/${id}/partenza`, method: 'POST', body: { data: d, ora } });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/soccorritori/${id}/partenza`, { data: d, ora }, { headers });
    },
    markRientro: async (id) => {
      const { data: d, ora } = localNow();
      if (isOffline()) {
        await enqueue('markRientro', { url: `${API}/soccorritori/${id}/rientro`, method: 'POST', body: { data: d, ora } });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/soccorritori/${id}/rientro`, { data: d, ora }, { headers });
    },
    addTurnoPresenza: async (id, data, ora) => {
      if (isOffline()) {
        await enqueue('addTurnoPresenza', { url: `${API}/soccorritori/${id}/aggiungi-turno`, method: 'POST', body: { data, ora } });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/soccorritori/${id}/aggiungi-turno`, { data, ora }, { headers });
    },
    assignAsTas2: async (id) => {
      if (isOffline()) {
        await enqueue('assignAsTas2', { url: `${API}/soccorritori/${id}/assign-tas2`, method: 'POST', body: null });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/soccorritori/${id}/assign-tas2`, null, { headers });
    },
    deleteSoccorritore: async (id) => {
      if (isOffline()) {
        // Creato offline e non ancora sincronizzato → rimuovi la create dalla coda
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteSoccorritore', { url: `${API}/soccorritori/${id}`, method: 'DELETE' });
        } else {
          await enqueue('deleteSoccorritore', { url: `${API}/soccorritori/${id}`, method: 'DELETE' });
        }
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/soccorritori/${id}`, { headers });
    },
    
    // Squadre
    getSquadre: (operazioneId) => axios.get(`${API}/squadre`, { params: { operazione_id: operazioneId }, headers }),
    getAvailableSquadre: (operazioneId) => axios.get(`${API}/squadre/available`, { params: { operazione_id: operazioneId }, headers }),
    createSquadra: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createSquadra', { url: `${API}/squadre`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, team_leader_nome: '', missione_attuale_id: null, componenti_ids: data.componenti_ids || [], _offline: true } };
      }
      return axios.post(`${API}/squadre`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    updateSquadra: async (id, data) => {
      if (isOffline()) {
        if (String(id).startsWith('offline_')) {
          const merged = await coalesceUpdate(id, data);
          if (!merged) await enqueue('updateSquadra', { url: `${API}/squadre/${id}`, method: 'PUT', body: data });
        } else {
          await enqueue('updateSquadra', { url: `${API}/squadre/${id}`, method: 'PUT', body: data });
        }
        return { data: { ...data, id, _offline: true } };
      }
      return axios.put(`${API}/squadre/${id}`, data, { headers });
    },
    deleteSquadra: async (id) => {
      if (isOffline()) {
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteSquadra', { url: `${API}/squadre/${id}`, method: 'DELETE' });
        } else {
          await enqueue('deleteSquadra', { url: `${API}/squadre/${id}`, method: 'DELETE' });
        }
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/squadre/${id}`, { headers });
    },
    
    // Missioni
    getMissioni: (operazioneId) => axios.get(`${API}/missioni`, { params: { operazione_id: operazioneId }, headers }),
    getMissione: (id) => axios.get(`${API}/missioni/${id}`, { headers }),
    checkDuplicate: (tipo, numero, operazioneId) => axios.get(`${API}/missioni/check-duplicate`, { params: { tipo, numero, operazione_id: operazioneId }, headers }),
    createMissione: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createMissione', { url: `${API}/missioni`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, nome: `${data.tipo}${data.numero}`, stato: 'DA_BATTERE', squadra_id: null, squadra_sigla: '', storico: [], _offline: true } };
      }
      return axios.post(`${API}/missioni`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    updateMissioneStato: async (id, stato, squadraId, assegnazioni) => {
      const bodyDisp = (assegnazioni && assegnazioni.length) ? { assegnazioni } : null;
      if (isOffline()) {
        await enqueue('updateMissioneStato', {
          url:    `${API}/missioni/${id}/stato`,
          method: 'PUT',
          params: { stato, squadra_id: squadraId },
          body:   bodyDisp || undefined,
        });
        return { data: { _offline: true } };
      }
      return axios.put(`${API}/missioni/${id}/stato`, bodyDisp, { params: { stato, squadra_id: squadraId }, headers });
    },
    patchMissione: async (id, data) => {
      if (isOffline()) {
        if (String(id).startsWith('offline_')) {
          const merged = await coalesceUpdate(id, data);
          if (!merged) await enqueue('patchMissione', { url: `${API}/missioni/${id}`, method: 'PATCH', body: data });
        } else {
          await enqueue('patchMissione', { url: `${API}/missioni/${id}`, method: 'PATCH', body: data });
        }
        return { data: { ...data, id, _offline: true } };
      }
      return axios.patch(`${API}/missioni/${id}`, data, { headers });
    },
    deleteMissione: async (id) => {
      if (isOffline()) {
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteMissione', { url: `${API}/missioni/${id}`, method: 'DELETE' });
        } else {
          await enqueue('deleteMissione', { url: `${API}/missioni/${id}`, method: 'DELETE' });
        }
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/missioni/${id}`, { headers });
    },

    // Disegni della mappa — poligoni, linee, punti e cerchi tracciati a mano.
    // Dato dell'operazione (tabella `disegni`, migr. 053), non piu' localStorage.
    getDisegni: (operazioneId) => axios.get(`${API}/disegni`, { params: { operazione_id: operazioneId }, headers }),
    createDisegno: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createDisegno', { url: `${API}/disegni`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, _offline: true } };
      }
      return axios.post(`${API}/disegni`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    patchDisegno: async (id, data) => {
      if (isOffline()) {
        // Disegno creato offline e non ancora sincronizzato: la modifica si fonde
        // nella create in coda, invece di partire verso un id che il server non
        // conosce.
        if (String(id).startsWith('offline_')) {
          const merged = await coalesceUpdate(id, data);
          if (!merged) await enqueue('patchDisegno', { url: `${API}/disegni/${id}`, method: 'PATCH', body: data });
        } else {
          await enqueue('patchDisegno', { url: `${API}/disegni/${id}`, method: 'PATCH', body: data });
        }
        return { data: { ...data, id, _offline: true } };
      }
      return axios.patch(`${API}/disegni/${id}`, data, { headers });
    },
    deleteDisegno: async (id) => {
      if (isOffline()) {
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteDisegno', { url: `${API}/disegni/${id}`, method: 'DELETE' });
        } else {
          await enqueue('deleteDisegno', { url: `${API}/disegni/${id}`, method: 'DELETE' });
        }
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/disegni/${id}`, { headers });
    },
    // Tracce GPX — percorso realmente fatto da una squadra. Dato dell'operazione
    // (tabella `tracce`, migr. 051), non piu' localStorage: la vede chiunque
    // abbia accesso, come missioni e indizi.
    getTracce: (operazioneId) => axios.get(`${API}/tracce`, { params: { operazione_id: operazioneId }, headers }),
    createTraccia: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createTraccia', { url: `${API}/tracce`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, _offline: true } };
      }
      return axios.post(`${API}/tracce`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    deleteTraccia: async (id) => {
      if (isOffline()) {
        // Traccia importata offline e non ancora sincronizzata: si toglie dalla
        // coda invece di accodare la cancellazione di qualcosa che non esiste.
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteTraccia', { url: `${API}/tracce/${id}`, method: 'DELETE' });
        } else {
          await enqueue('deleteTraccia', { url: `${API}/tracce/${id}`, method: 'DELETE' });
        }
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/tracce/${id}`, { headers });
    },
    // Dispositivi (GPS / RADIO) — inventario per operazione + tracciabilità
    getDispositivi: (operazioneId) => axios.get(`${API}/dispositivi`, { params: { operazione_id: operazioneId }, headers }),
    createDispositivo: (data) => axios.post(`${API}/dispositivi`, data, { headers }),
    updateDispositivo: (id, data) => axios.patch(`${API}/dispositivi/${id}`, data, { headers }),
    deleteDispositivo: (id) => axios.delete(`${API}/dispositivi/${id}`, { headers }),

    // Turni
    getTurni: (operazioneId) => axios.get(`${API}/turni`, { params: { operazione_id: operazioneId }, headers }),
    getActiveTurno: (operazioneId) => axios.get(`${API}/turni/active`, { params: { operazione_id: operazioneId }, headers }),
    createTurno: (data) => axios.post(`${API}/turni`, data, { headers }),
    updateTurno: (id, data) => axios.put(`${API}/turni/${id}`, data, { headers }),
    patchTurno:  (id, data) => axios.patch(`${API}/turni/${id}`, data, { headers }),
    closeTurno: (id) => axios.post(`${API}/turni/${id}/close`, null, { headers }),
    deleteTurno: (id) => axios.delete(`${API}/turni/${id}`, { headers }),
    // Cambio turno operatore (passaggio di consegne sulla stessa postazione).
    // Funziona offline: viene accodato e attribuito al sync.
    cambioOperatoreTurno: async (data) => {
      if (isOffline()) {
        await enqueue('cambioOperatoreTurno', { url: `${API}/turni/cambio-operatore`, method: 'POST', body: data, acting: data?.operatore_in_turno || null });
        return { data: { _offline: true, ...data } };
      }
      return axios.post(`${API}/turni/cambio-operatore`, data, { headers });
    },

    // Comunicazioni
    getComunicazioni: (operazioneId) => axios.get(`${API}/comunicazioni`, { params: { operazione_id: operazioneId }, headers }),
    createComunicazione: async (data) => {
      const idem = idemKey();
      if (isOffline()) {
        // clientId stabile: lega la riga ottimistica alla POST in coda, così
        // modifiche/eliminazioni offline successive possono fondersi/rimuoverla.
        const cid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        await enqueue('createComunicazione', { url: `${API}/comunicazioni`, method: 'POST', body: data, clientId: cid, idempotencyKey: idem });
        return { data: { ...data, id: cid, _offline: true } };
      }
      return axios.post(`${API}/comunicazioni`, data, { headers: { ...headers, 'Idempotency-Key': idem } });
    },
    patchComunicazione: async (id, data) => {
      if (isOffline()) {
        // Elemento creato offline e non ancora sincronizzato → fondi la modifica
        // nella POST in coda (niente PATCH verso un id che il server non conosce).
        if (String(id).startsWith('offline_')) {
          const merged = await coalesceUpdate(id, data);
          if (!merged) await enqueue('patchComunicazione', { url: `${API}/comunicazioni/${id}`, method: 'PATCH', body: data });
          return { data: { ...data, id, _offline: true } };
        }
        await enqueue('patchComunicazione', { url: `${API}/comunicazioni/${id}`, method: 'PATCH', body: data });
        return { data: { ...data, id, _offline: true } };
      }
      return axios.patch(`${API}/comunicazioni/${id}`, data, { headers });
    },
    deleteComunicazione: async (id) => {
      if (isOffline()) {
        // Elemento creato offline e non ancora sincronizzato → rimuovi la create
        // dalla coda: non deve essere creato affatto sul server.
        if (String(id).startsWith('offline_')) {
          const dropped = await dropPending(id);
          if (!dropped) await enqueue('deleteComunicazione', { url: `${API}/comunicazioni/${id}`, method: 'DELETE' });
          return { data: { _offline: true } };
        }
        await enqueue('deleteComunicazione', { url: `${API}/comunicazioni/${id}`, method: 'DELETE' });
        return { data: { _offline: true } };
      }
      return axios.delete(`${API}/comunicazioni/${id}`, { headers });
    },
    // Fase 3a - Indizi POA (sottotipo di comunicazione)
    applicaIndizioPoa:  (comId, body = null)   => axios.post(`${API}/comunicazioni/${comId}/applica-poa`, body, { headers }),
    listIndiziOperazione: (operazioneId)       => axios.get(`${API}/operazioni/${operazioneId}/indizi`, { headers }),
    
    // Logs
    getLogs: (operazioneId, limit) => axios.get(`${API}/logs`, { params: { operazione_id: operazioneId, limit }, headers }),
    // Logs paginati server-side con filtri
    // params: { operazione_id, page, page_size, tipo, entita, operatore_email, data_da, data_a, q }
    getLogsPaginated: (params) => axios.get(`${API}/logs`, { params: { paginated: true, page_size: 50, ...params }, headers }),
    getAccessLogs: (limit) => axios.get(`${API}/logs/access`, { params: { limit }, headers }),
    
    // Import
    importSoccorritori: (data) => axios.post(`${API}/import/soccorritori`, data, { headers }),
    
    // Stats
    getStats: (operazioneId) => axios.get(`${API}/stats/${operazioneId}`, { headers }),

    // Profilo
    getProfilo:    ()     => axios.get(`${API}/profilo`, { headers }),
    updateProfilo: (data) => axios.put(`${API}/profilo`, data, { headers }),
    cambiaPassword: (data) => axios.post(`${API}/profilo/cambia-password`, data, { headers }),

    // Documenti supporto
    getDocumenti:    ()     => axios.get(`${API}/documenti`, { headers }),
    getDocumento:    (id)   => axios.get(`${API}/documenti/${id}`, { headers }),
    uploadDocumento: (data) => axios.post(`${API}/documenti`, data, { headers }),
    deleteDocumento: (id)   => axios.delete(`${API}/documenti/${id}`, { headers }),

    // Richiesta di assistenza allo staff (il destinatario lo decide il backend)
    getContattiSupporto:   ()     => axios.get(`${API}/supporto/contatti`, { headers }),
    inviaRichiestaSupporto: (data) => axios.post(`${API}/supporto/richiesta`, data, { headers }),

    // Avvisi (campanella in Dashboard). Lo stato di lettura è per-operatore
    // lato server, non in localStorage: vale su tutti i dispositivi.
    getAvvisi:          ()   => axios.get(`${API}/avvisi`, { headers }),
    getAvvisiNonLetti:  ()   => axios.get(`${API}/avvisi/non-letti`, { headers }),
    segnaAvvisiLetti:   ()   => axios.put(`${API}/avvisi/letti`, {}, { headers }),

    // Meteo reale sulle coordinate PCA (Open-Meteo via backend: dal browser
    // richiederebbe di allentare la CSP). Non gated dal flag POA.
    getMeteo: (operazioneId) => axios.get(`${API}/operazioni/${operazioneId}/meteo`, { headers }),

    // Scheda Disperso
    getSchedaDisperso: (operazioneId) => axios.get(`${API}/operazioni/${operazioneId}/scheda-disperso`, { headers }),
    putSchedaDisperso: async (operazioneId, data) => {
      if (isOffline()) {
        await enqueue('putSchedaDisperso', { url: `${API}/operazioni/${operazioneId}/scheda-disperso`, method: 'PUT', body: data });
        return { data: { _offline: true } };
      }
      return axios.put(`${API}/operazioni/${operazioneId}/scheda-disperso`, data, { headers });
    },

    // Schede preliminari — la Scheda Disperso compilata dalla SALA OPERATIVA
    // prima che l'operazione esista. Nessuna di queste passa dalla coda offline:
    // la sala operativa lavora da una postazione fissa con la rete, e una scheda
    // in coda che risulta "inserita" senza esserlo manderebbe il TAS sul posto a
    // cercare dati che nessuno ha ancora ricevuto.
    getSchedePreliminari:    (comandoVvf) => axios.get(`${API}/schede-preliminari`, { headers, params: comandoVvf ? { comando_vvf: comandoVvf } : {} }),
    getSchedaPreliminare:    (id)         => axios.get(`${API}/schede-preliminari/${id}`, { headers }),
    createSchedaPreliminare: (data)       => axios.post(`${API}/schede-preliminari`, data, { headers }),
    updateSchedaPreliminare: (id, data)   => axios.put(`${API}/schede-preliminari/${id}`, data, { headers }),
    deleteSchedaPreliminare: (id)         => axios.delete(`${API}/schede-preliminari/${id}`, { headers }),

    // POA — Profili ISRID + calcolo Bayesiano
    getProfiliIsrid:    ()                             => axios.get(`${API}/profili-isrid`, { headers }),
    calcolaPoa:         (operazioneId)                 => axios.post(`${API}/operazioni/${operazioneId}/poa/calcola`, null, { headers }),
    aggiungiZonePoa:    (operazioneId)                 => axios.post(`${API}/operazioni/${operazioneId}/poa/aggiungi-zone`, null, { headers }),
    getPoa:             (operazioneId)                 => axios.get(`${API}/operazioni/${operazioneId}/poa`, { headers }),
    ricercaNegativa:    async (missioneId, pod) => {
      if (isOffline()) {
        await enqueue('ricercaNegativa', { url: `${API}/missioni/${missioneId}/poa/ricerca-negativa`, method: 'POST', body: { pod } });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/missioni/${missioneId}/poa/ricerca-negativa`, { pod }, { headers });
    },
    importZoneGm:       async (operazioneId, formData) => {
      if (isOffline()) {
        // Offline: accoda l'upload del file; al sync il backend fa l'import reale.
        // La visualizzazione ottimistica delle zone la gestisce la pagina (parse client-side).
        const file = formData.get('file');
        await enqueue('importZoneGm', { url: `${API}/operazioni/${operazioneId}/zone/import-gm`, method: 'POST', file, fileField: 'file' });
        return { data: { _offline: true } };
      }
      return axios.post(`${API}/operazioni/${operazioneId}/zone/import-gm`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
    },
    exportPoaKmlUrl:    (operazioneId)                 => `${API}/operazioni/${operazioneId}/poa/export-kml`,

    // Analisi celle telefoniche (area probabile dispositivo) — overlay mappa + KML
    analizzaCelle:      (operazioneId, formData)       => axios.post(`${API}/operazioni/${operazioneId}/celle/analizza`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } }),
    analizzaCelleManuale: (operazioneId, payload)      => axios.post(`${API}/operazioni/${operazioneId}/celle/analizza-manuale`, payload, { headers }),
    listCelle:          (operazioneId)                 => axios.get(`${API}/operazioni/${operazioneId}/celle`, { headers }),
    deleteCelle:        (operazioneId, cellaId)        => axios.delete(`${API}/operazioni/${operazioneId}/celle/${cellaId}`, { headers }),
    exportCelleKml:     (operazioneId, cellaId)        => axios.get(`${API}/operazioni/${operazioneId}/celle/${cellaId}/export-kml`, { headers, responseType: 'blob' }),

    // POA — Area di Ricerca + Heatmap (Fase 1b)
    getAreaRicerca:        (operazioneId)              => axios.get(`${API}/operazioni/${operazioneId}/area-ricerca`, { headers }),
    generaCerchioArea:     (operazioneId, raggio_metri, nome) => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/genera-cerchio`, { raggio_metri, nome }, { headers }),
    importAreaGm:          (operazioneId, formData)    => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/import-gm`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } }),
    deleteAreaRicerca:     (operazioneId)              => axios.delete(`${API}/operazioni/${operazioneId}/area-ricerca`, { headers }),
    calcolaHeatmap:        (operazioneId, cell_size_metri) => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/heatmap/calcola`, { cell_size_metri }, { headers }),
    // POA — DTM via CSV (Fase 1c, opzione C)
    importCsvDtm:          (operazioneId, formData)    => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/heatmap/import-csv-dtm`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } }),
    // POA — OpenStreetMap land use (Fase 1d, flusso esplicito)
    fetchOsmLandUse:       (operazioneId)              => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/heatmap/fetch-osm-landuse`, null, { headers }),
    ricalcolaConOsm:       (operazioneId)              => axios.post(`${API}/operazioni/${operazioneId}/area-ricerca/heatmap/ricalcola-con-osm`, null, { headers }),
  };
};

// Enums
const ENTI = [
  "Vigili del Fuoco", "Polizia di Stato", "Carabinieri", "CC Forestali", "Guardia di Finanza",
  "Protezione Civile", "Soccorso Alpino", "118", "Misericordia", "Croce Rossa", "SMOM",
  "Polizia Provinciale", "Vigili Urbani", "Volontari", "Esercito", "Civili", "Altro"
];

// Regioni → province italiane (107 province / città metropolitane / liberi consorzi).
// Usato per i due campi a cascata "Regione/Provincia di provenienza" in autoregistrazione.
const REGIONI_PROVINCE = {
  "Abruzzo": ["Chieti", "L'Aquila", "Pescara", "Teramo"],
  "Basilicata": ["Matera", "Potenza"],
  "Calabria": ["Catanzaro", "Cosenza", "Crotone", "Reggio Calabria", "Vibo Valentia"],
  "Campania": ["Avellino", "Benevento", "Caserta", "Napoli", "Salerno"],
  "Emilia-Romagna": ["Bologna", "Ferrara", "Forlì-Cesena", "Modena", "Parma", "Piacenza", "Ravenna", "Reggio Emilia", "Rimini"],
  "Friuli-Venezia Giulia": ["Gorizia", "Pordenone", "Trieste", "Udine"],
  "Lazio": ["Frosinone", "Latina", "Rieti", "Roma", "Viterbo"],
  "Liguria": ["Genova", "Imperia", "La Spezia", "Savona"],
  "Lombardia": ["Bergamo", "Brescia", "Como", "Cremona", "Lecco", "Lodi", "Mantova", "Milano", "Monza e della Brianza", "Pavia", "Sondrio", "Varese"],
  "Marche": ["Ancona", "Ascoli Piceno", "Fermo", "Macerata", "Pesaro e Urbino"],
  "Molise": ["Campobasso", "Isernia"],
  "Piemonte": ["Alessandria", "Asti", "Biella", "Cuneo", "Novara", "Torino", "Verbano-Cusio-Ossola", "Vercelli"],
  "Puglia": ["Bari", "Barletta-Andria-Trani", "Brindisi", "Foggia", "Lecce", "Taranto"],
  "Sardegna": ["Cagliari", "Nuoro", "Oristano", "Sassari", "Sud Sardegna"],
  "Sicilia": ["Agrigento", "Caltanissetta", "Catania", "Enna", "Messina", "Palermo", "Ragusa", "Siracusa", "Trapani"],
  "Toscana": ["Arezzo", "Firenze", "Grosseto", "Livorno", "Lucca", "Massa-Carrara", "Pisa", "Pistoia", "Prato", "Siena"],
  "Trentino-Alto Adige": ["Bolzano", "Trento"],
  "Umbria": ["Perugia", "Terni"],
  "Valle d'Aosta": ["Aosta"],
  "Veneto": ["Belluno", "Padova", "Rovigo", "Treviso", "Venezia", "Verona", "Vicenza"],
};

const SPECIALIZZAZIONI = [
  "TAS 1", "TAS 2", "SAR", "SAF", "SAGF", "SA", "Cinofili VVF", "Cinofili",
  "Sommozzatori VVF", "Sommozzatori", "Elicottero VVF", "Elicottero",
  "Fluviali VVF", "Fluviali", "SAPR", "DTS/ROS", "TLC", "Portuali", "USAR M", "USAR L", "ALTRI"
];

const TIPI_SQUADRA = ["SAR", "CINO", "SAF", "SAGF", "SMZ", "ELI", "FLU", "TAS", "SA", "SAPR", "ALTRO"];
const TIPI_MISSIONE = [
  { value: "Z", label: "Zona" },
  { value: "L", label: "Linea" },
  { value: "P", label: "Punto" }
];

const STATI_MISSIONE = {
  DA_BATTERE: { label: "Da Battere", class: "status-da-battere" },
  IN_BATTUTA: { label: "In Battuta", class: "status-in-battuta" },
  BATTUTA: { label: "Battuta", class: "status-battuta" }
};

// Classificazione terreno per il calcolo POA.
// I value devono matchare le chiavi terrain_preferences nei profili ISRID
// del seed (migration 018). Se il terreno non e' presente nel dict del
// profilo selezionato, il backend usa peso 1.0 (neutro, non penalizzante).
const TIPI_TERRENO = [
  { value: "",                label: "— non classificato —" },
  { value: "trail",           label: "Sentiero (segnato)" },
  { value: "trail_marked",    label: "Sentiero (battuto)" },
  { value: "forest_light",    label: "Bosco rado" },
  { value: "forest_dense",    label: "Bosco fitto" },
  { value: "forest_managed",  label: "Bosco gestito" },
  { value: "ridge",           label: "Cresta / punto panoramico" },
  { value: "rocky_steep",     label: "Roccia / parete" },
  { value: "cliff",           label: "Scarpata / strapiombo" },
  { value: "water",           label: "Acqua (lago/specchio)" },
  { value: "river_bank",      label: "Riva fiume / corso d'acqua" },
  { value: "open_field",      label: "Prato / campo aperto" },
  { value: "agricultural",    label: "Area agricola" },
  { value: "urban_area",      label: "Area urbanizzata" },
  { value: "road",            label: "Strada / carrabile" },
  { value: "road_paved",      label: "Strada asfaltata" },
  { value: "off_trail",       label: "Fuori sentiero" },
  { value: "cave_entrance",   label: "Ingresso grotta" },
];

// CHE COSA e' la comunicazione. Prima questa lista teneva insieme la natura e il
// canale (Radio/Telefono/... e Indizio): con i punti non reggeva piu', perche' un
// punto puo' arrivare via radio o di persona. Il canale sta in MODALITA_COMUNICAZIONE.
const TIPI_COMUNICAZIONE = [
  // Indizio georeferenziato: punto viola in mappa, e con POA attivo modula il
  // calcolo via aggiornamento Bayesiano (Fase 3a).
  { value: "INDIZIO",      label: "Indizio",      icon: MapPin },
  // Punto notevole: la mappa ci disegna un punto, nessun effetto sul POA.
  { value: "PUNTO",        label: "Punto",        icon: MapPin },
  // Non crea niente: resta una riga del cronologico.
  { value: "INFORMAZIONE", label: "Informazione", icon: MessageSquare },
];

// COME e' arrivata la comunicazione.
const MODALITA_COMUNICAZIONE = [
  { value: "RADIO",    label: "Radio",    icon: Radio },
  { value: "TELEFONO", label: "Telefono", icon: Phone },
  { value: "FRONTALE", label: "Frontale", icon: MessageSquare },
  { value: "ALTRO",    label: "Altro",    icon: Megaphone },
];

const PRIORITA_COMUNICAZIONE = [
  { value: "URGENTE", label: "Urgente", class: "badge-red" },
  { value: "NORMALE", label: "Normale", class: "badge-yellow" },
  { value: "INFO", label: "Info", class: "badge-blue" }
];

// Etichette di PRESENTAZIONE degli stati soccorritore. I valori nel DB restano
// ATTIVO/PARTITO/RIENTRATO/PENDING — qui cambia solo come vengono mostrati:
// ATTIVO e RIENTRATO sono entrambi "Disponibile" (chi rientra da una missione torna
// disponibile a tutti gli effetti), PARTITO e' "Non disponibile".
const STATI_SOCCORRITORE = {
  ATTIVO: { label: "Disponibile", class: "badge-green" },
  PARTITO: { label: "Non disponibile", class: "badge-yellow" },
  RIENTRATO: { label: "Disponibile", class: "badge-green" },
  PENDING: { label: "In Attesa", class: "badge-yellow" }
};

// PDF Generation
const generatePDF = {
  // Intestazione istituzionale con logo, testo dipartimento e titolo sezione
  addInstitutionalHeader: (doc, operazione, title, logoBase64, landscape) => {
    const W = landscape ? 297 : 210;
    const ML = 10, CW = W - 20;
    const ltype = logoBase64 ? (logoBase64.startsWith('data:image/png') ? 'PNG' : 'JPEG') : null;
    const hY = 8, hH = 32, logoAreaW = 34;
    doc.rect(ML, hY, CW, hH);
    doc.line(ML + logoAreaW, hY, ML + logoAreaW, hY + hH);
    doc.line(W - ML - logoAreaW, hY, W - ML - logoAreaW, hY + hH);
    if (ltype) {
      try {
        const props = doc.getImageProperties(logoBase64);
        const maxSz = 28;
        const scale = Math.min(maxSz / props.width, maxSz / props.height);
        const dW = props.width * scale, dH = props.height * scale;
        const offX = (maxSz - dW) / 2, offY = (maxSz - dH) / 2;
        doc.addImage(logoBase64, ltype, ML + 3 + offX, hY + 2 + offY, dW, dH);
        doc.addImage(logoBase64, ltype, W - ML - logoAreaW + 3 + offX, hY + 2 + offY, dW, dH);
      } catch {}
    }
    const cX = ML + logoAreaW + (CW - 2 * logoAreaW) / 2;
    const tMaxW = CW - 2 * logoAreaW - 4;
    doc.setFont(undefined, 'bold'); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
    doc.text('DIPARTIMENTO DEI VIGILI DEL FUOCO', cX, hY + 9, { align: 'center' });
    doc.text('DEL SOCCORSO PUBBLICO E DELLA DIFESA CIVILE', cX, hY + 14, { align: 'center' });
    doc.setFont(undefined, 'normal'); doc.setFontSize(5.5);
    doc.text("DIREZIONE CENTRALE PER L'EMERGENZA, IL SOCCORSO TECNICO E L'ANTINCENDIO BOSCHIVO", cX, hY + 19, { align: 'center', maxWidth: tMaxW });
    doc.line(ML + logoAreaW + 1, hY + 21, W - ML - logoAreaW - 1, hY + 21);
    doc.setFont(undefined, 'bold'); doc.setFontSize(10); doc.setTextColor(0, 0, 0);
    doc.text(title, cX, hY + 29, { align: 'center' });
    // Operazione info bar
    const barY = hY + hH + 1;
    doc.setFillColor(243, 244, 246);
    doc.rect(ML, barY, CW, 7, 'F');
    doc.rect(ML, barY, CW, 7);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.setTextColor(60, 60, 60);
    doc.text(`Operazione: ${operazione.nome}  |  Località: ${operazione.localita}  |  Data: ${operazione.data_inizio}  |  Stampa: ${new Date().toLocaleString('it-IT')}`, cX, barY + 4.5, { align: 'center', maxWidth: CW - 4 });
    // Un PDF di esercitazione non deve poter essere confuso con uno di intervento
    // reale se finisce stampato in un fascicolo: banda ambra ben visibile.
    if (operazione.tipo === 'ESERCITAZIONE') {
      const eY = barY + 8;
      doc.setFillColor(254, 243, 199);
      doc.setDrawColor(245, 158, 11);
      doc.rect(ML, eY, CW, 6, 'FD');
      doc.setFont(undefined, 'bold'); doc.setFontSize(8); doc.setTextColor(146, 64, 14);
      doc.text('ESERCITAZIONE — dati non validi ai fini statistici', cX, eY + 4.2, { align: 'center' });
      doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0); doc.setDrawColor(0, 0, 0);
      return hY + hH + 8 + 4 + 7;
    }
    return hY + hH + 8 + 4; // y after header
  },

  addFooter: (doc, landscape) => {
    const W = landscape ? 297 : 210;
    const H = landscape ? 210 : 297;
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(6.5); doc.setTextColor(150);
      doc.text(`Pagina ${i} di ${pageCount}  —  TAS Command`, W / 2, H - 5, { align: 'center' });
    }
  },

  // Report aggregato (pannelli admin/ROP-ROR-RON/CON-DIR_CAM-COM_PROV).
  // Non è legato a un'operazione: header istituzionale + tabelle per sezione.
  report: async (data, opts = {}) => {
    const { title = 'Report Generale', subtitle = '', logoBase64 = null } = opts;
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    const W = 210, ML = 10, CW = W - 20;

    // ── Header istituzionale (riquadro con logo + dicitura dipartimento) ──
    const ltype = logoBase64 ? (logoBase64.startsWith('data:image/png') ? 'PNG' : 'JPEG') : null;
    const hY = 8, hH = 32, logoAreaW = 34;
    doc.rect(ML, hY, CW, hH);
    doc.line(ML + logoAreaW, hY, ML + logoAreaW, hY + hH);
    doc.line(W - ML - logoAreaW, hY, W - ML - logoAreaW, hY + hH);
    if (ltype) {
      try {
        const props = doc.getImageProperties(logoBase64);
        const maxSz = 28;
        const scale = Math.min(maxSz / props.width, maxSz / props.height);
        const dW = props.width * scale, dH = props.height * scale;
        const offX = (maxSz - dW) / 2, offY = (maxSz - dH) / 2;
        doc.addImage(logoBase64, ltype, ML + 3 + offX, hY + 2 + offY, dW, dH);
        doc.addImage(logoBase64, ltype, W - ML - logoAreaW + 3 + offX, hY + 2 + offY, dW, dH);
      } catch {}
    }
    const cX = ML + logoAreaW + (CW - 2 * logoAreaW) / 2;
    const tMaxW = CW - 2 * logoAreaW - 4;
    doc.setFont(undefined, 'bold'); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
    doc.text('DIPARTIMENTO DEI VIGILI DEL FUOCO', cX, hY + 9, { align: 'center' });
    doc.text('DEL SOCCORSO PUBBLICO E DELLA DIFESA CIVILE', cX, hY + 14, { align: 'center' });
    doc.setFont(undefined, 'normal'); doc.setFontSize(5.5);
    doc.text("DIREZIONE CENTRALE PER L'EMERGENZA, IL SOCCORSO TECNICO E L'ANTINCENDIO BOSCHIVO", cX, hY + 19, { align: 'center', maxWidth: tMaxW });
    doc.line(ML + logoAreaW + 1, hY + 21, W - ML - logoAreaW - 1, hY + 21);
    doc.setFont(undefined, 'bold'); doc.setFontSize(10);
    doc.text(String(title).toUpperCase(), cX, hY + 29, { align: 'center' });
    const barY = hY + hH + 1;
    doc.setFillColor(243, 244, 246);
    doc.rect(ML, barY, CW, 7, 'F');
    doc.rect(ML, barY, CW, 7);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.setTextColor(60, 60, 60);
    doc.text(`${subtitle ? subtitle + '  |  ' : ''}Stampa: ${new Date().toLocaleString('it-IT')}`, cX, barY + 4.5, { align: 'center', maxWidth: CW - 4 });
    let y = barY + 7 + 6;

    const t = data.totali || {};
    const dsp = data.dispersi || {};

    // KPI principali
    autoTable(doc, {
      startY: y,
      head: [['Operazioni totali', 'Schede Disperso', 'Op. Attive', 'Op. Chiuse']],
      body: [[t.operazioni_totali ?? 0, t.schede_disperso ?? 0, t.operazioni_attive ?? 0, t.operazioni_chiuse ?? 0]],
      theme: 'grid', styles: { halign: 'center', fontSize: 11, fontStyle: 'bold' },
      headStyles: { fillColor: [31, 41, 55], textColor: 255, fontSize: 7, fontStyle: 'bold' },
      margin: { left: ML, right: ML },
    });
    y = doc.lastAutoTable.finalY + 8;

    const pctRows = (arr) => {
      const tot = (arr || []).reduce((s, d) => s + (d.count || 0), 0);
      return (arr || []).map(d => [d.label, d.count, tot > 0 ? `${Math.round((d.count / tot) * 100)}%` : '0%']);
    };
    const addSection = (titleText, head, body, note) => {
      if (!body || !body.length) return;
      if (y > 262) { doc.addPage(); y = 15; }
      doc.setFont(undefined, 'bold'); doc.setFontSize(9); doc.setTextColor(20, 20, 20);
      doc.text(titleText, ML, y); y += 2;
      autoTable(doc, {
        startY: y + 1, head: [head], body,
        theme: 'striped', styles: { fontSize: 8 },
        headStyles: { fillColor: [55, 65, 81], textColor: 255, fontSize: 8 },
        margin: { left: ML, right: ML },
      });
      y = doc.lastAutoTable.finalY + (note ? 1 : 7);
      if (note) {
        doc.setFont(undefined, 'italic'); doc.setFontSize(7.5); doc.setTextColor(90, 90, 90);
        doc.text(note, ML, y + 4); y += 10;
        doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
      }
    };

    // Trend mensile
    if ((data.trend_mensile || []).length) {
      const peak = data.trend_mensile.reduce((a, b) => (b.count > a.count ? b : a), data.trend_mensile[0]);
      addSection('Trend interventi per mese', ['Mese', 'Interventi'],
        data.trend_mensile.map(d => [d.mese, d.count]),
        `Mese di punta: ${peak.mese} (${peak.count} interventi).`);
    }
    // Operazioni per regione
    if ((data.per_regione || []).length) {
      const tot = data.per_regione.reduce((s, d) => s + d.count, 0);
      const top = data.per_regione.reduce((a, b) => (b.count > a.count ? b : a), data.per_regione[0]);
      addSection('Operazioni per regione', ['Regione', 'Casi', '% sul totale'], pctRows(data.per_regione),
        `Regione con più casi: ${top.label} (${tot > 0 ? Math.round((top.count / tot) * 100) : 0}% del totale).`);
    }
    // Dispersi per profilo ISRID
    if ((dsp.per_profilo_isrid || []).length) {
      const tot = dsp.per_profilo_isrid.reduce((s, d) => s + d.count, 0);
      const top = dsp.per_profilo_isrid.reduce((a, b) => (b.count > a.count ? b : a), dsp.per_profilo_isrid[0]);
      addSection('Dispersi per profilo ISRID', ['Profilo', 'Casi', '% dispersi'], pctRows(dsp.per_profilo_isrid),
        `Profilo più frequente: ${top.label} (${tot > 0 ? Math.round((top.count / tot) * 100) : 0}% dei dispersi).`);
    }
    // Demografica
    addSection('Dispersi per sesso', ['Sesso', 'N', '%'], pctRows(dsp.per_sesso));
    addSection("Dispersi per fascia d'età", ['Fascia', 'N', '%'], pctRows(dsp.per_eta));
    addSection('Esiti operazioni di ricerca', ['Esito', 'N', '%'], pctRows(dsp.per_esito));
    addSection('Salvataggi e ritrovamenti', ['Tipo', 'N', '% esiti positivi'], pctRows(dsp.per_esito_positivo),
      'Salvataggio: persona ritrovata viva (INDIA 0-3). Ritrovamento: persona deceduta (INDIA 4).');
    addSection('Distribuzione codice INDIA', ['Codice', 'N', '%'], pctRows(dsp.per_codice_india));
    addSection('Dispersi con problemi mentali / cognitivi', ['Categoria', 'N', '%'], pctRows(dsp.per_problemi_mentali));

    generatePDF.addFooter(doc, false);
    const fname = `report_${String(title).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}.pdf`;
    doc.save(fname);
  },

  // Legacy header kept for backward compatibility
  addHeader: (doc, operazione, title) => {
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text("DIPARTIMENTO DEI VIGILI DEL FUOCO DEL SOCCORSO PUBBLICO E DELLA DIFESA CIVILE", 105, 15, { align: "center" });
    doc.text("DIREZIONE CENTRALE PER L'EMERGENZA, IL SOCCORSO TECNICO E L'ANTINCENDIO BOSCHIVO", 105, 20, { align: "center" });
    doc.setFontSize(16); doc.setTextColor(0); doc.setFont(undefined, "bold");
    doc.text(title, 105, 35, { align: "center" });
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    doc.text(`Operazione: ${operazione.nome}`, 20, 45);
    doc.text(`Località: ${operazione.localita}`, 20, 50);
    doc.text(`Data: ${operazione.data_inizio}`, 150, 45);
    doc.text(`Turno: ${operazione.turno_tas || "-"}`, 150, 50);
    return 60;
  },

  dashboard: async (operazione, logoBase64, soccorritori = [], missioni = [], squadre = []) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    const W = 210, ML = 10, CW = W - 20;
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'RIEPILOGO OPERAZIONE', logoBase64, false);

    // ── Dati operazione ──────────────────────────────────────────────────────
    const coordStr = (operazione.lat && operazione.lon)
      ? `${Number(operazione.lat).toFixed(6)}, ${Number(operazione.lon).toFixed(6)}`
      : '—';
    autoTable(doc, {
      startY: y,
      body: [
        ['Nome operazione', operazione.nome || '—'],
        ['Intervento', operazione.intervento || '—'],
        ['Comando VVF', operazione.comando_vvf || '—'],
        ['N° Scheda SO115', operazione.numero_scheda_so115 || '—'],
        ['Località', operazione.localita || '—'],
        ['Regione', operazione.regione || '—'],
        ['Coordinate GPS', coordStr],
        ['Data inizio', operazione.data_inizio || '—'],
        ['Ora inizio', operazione.ora_inizio || '—'],
        ['Turno TAS', operazione.turno_tas || '—'],
        ['Funzionario di servizio', operazione.funzionario_servizio || '—'],
        ['Capo turno', operazione.capo_turno || '—'],
        ['Telefono PCA', operazione.telefono_pca || '—'],
        ['Canale UHF', operazione.canale_uhf || '—'],
        ['Canale VHF', operazione.canale_vhf || '—'],
        ['TAS2 – Posizione 1', operazione.tas2_1_nome || '—'],
        ['TAS2 – Posizione 2', operazione.tas2_2_nome || '—'],
        ['Note', operazione.note || '—'],
      ],
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      tableWidth: 190,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65, fillColor: [243, 244, 246] } },
    });
    y = doc.lastAutoTable.finalY + 6;

    // ── Statistiche operative (grafiche) ─────────────────────────────────────
    const attivi    = soccorritori.filter(s => s.stato === 'ATTIVO').length;
    const daBattere = missioni.filter(m => m.stato === 'DA_BATTERE').length;
    const inBattuta = missioni.filter(m => m.stato === 'IN_BATTUTA').length;
    const battute   = missioni.filter(m => m.stato === 'BATTUTA').length;

    const statCards = [
      { label: 'SOCCORRITORI\nATTIVI',    value: attivi,        color: [249, 115, 22]  },
      { label: 'SQUADRE\nOPERATIVE',      value: squadre.length, color: [59,  130, 246] },
      { label: 'MISSIONI\nDA BATTERE',    value: daBattere,     color: [239,  68,  68] },
      { label: 'MISSIONI\nIN BATTUTA',    value: inBattuta,     color: [234, 179,   8] },
      { label: 'MISSIONI\nBATTUTE',       value: battute,       color: [34,  197,  94] },
    ];

    // Section header bar (light — ottimizzato per stampa)
    doc.setFillColor(235, 239, 244);
    doc.rect(ML, y, CW, 7, 'F');
    doc.setDrawColor(200, 210, 220);
    doc.rect(ML, y, CW, 7, 'S');
    doc.setFontSize(7.5); doc.setTextColor(30, 41, 59); doc.setFont(undefined, 'bold');
    doc.text('STATISTICHE OPERATIVE', ML + 3, y + 5);
    y += 10;

    const cardW = 34, cardH = 30, cardGap = 4;
    const totalW = statCards.length * cardW + (statCards.length - 1) * cardGap;
    const startX = ML + (CW - totalW) / 2;

    statCards.forEach((card, i) => {
      const cx = startX + i * (cardW + cardGap);
      // Card background (white) + bordo grigio
      doc.setFillColor(255, 255, 255);
      doc.rect(cx, y, cardW, cardH, 'F');
      doc.setDrawColor(210, 218, 226);
      doc.rect(cx, y, cardW, cardH, 'S');
      // Colored top strip (3mm)
      doc.setFillColor(...card.color);
      doc.rect(cx, y, cardW, 3, 'F');
      // Value number (dark)
      doc.setFontSize(20); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
      doc.text(card.value.toString(), cx + cardW / 2, y + 17, { align: 'center' });
      // Label (two lines, small, gray)
      doc.setFontSize(5); doc.setFont(undefined, 'normal'); doc.setTextColor(100, 116, 139);
      const lines = card.label.split('\n');
      lines.forEach((line, li) => {
        doc.text(line, cx + cardW / 2, y + 23 + li * 4, { align: 'center' });
      });
    });

    y += cardH + 8;

    // ── Copertura ricerca: superficie/percorsi battuti + avanzamento ─────────
    // "Battuta una volta" = stato finale BATTUTA (lo storico può contenere più
    // battute della stessa zona, qui contiamo la zona/linea una sola volta).
    const zBattute = missioni.filter(m => m.stato === 'BATTUTA' && m.tipo === 'Z');
    const lBattute = missioni.filter(m => m.stato === 'BATTUTA' && m.tipo === 'L');
    const areaKm2 = zBattute.reduce((s, m) => s + (m.area_km2 || 0), 0);
    const lunghM  = lBattute.reduce((s, m) => s + (m.lunghezza_m || 0), 0);
    const totMiss = missioni.length;
    const pctAvz  = totMiss > 0 ? Math.round((battute / totMiss) * 100) : 0;
    if (y > 250) { doc.addPage(); y = 15; }
    doc.setFillColor(235, 239, 244); doc.rect(ML, y, CW, 7, 'F');
    doc.setDrawColor(200, 210, 220); doc.rect(ML, y, CW, 7, 'S');
    doc.setFontSize(7.5); doc.setTextColor(30, 41, 59); doc.setFont(undefined, 'bold');
    doc.text('COPERTURA RICERCA', ML + 3, y + 5);
    y += 10;
    autoTable(doc, {
      startY: y,
      body: [
        ['Superficie battuta', `${(areaKm2 * 100).toFixed(2)} ha  (${areaKm2.toFixed(3)} km²)`, `${zBattute.length} zone`],
        ['Percorsi battuti', `${(lunghM / 1000).toFixed(2)} km  (${Math.round(lunghM)} m)`, `${lBattute.length} linee`],
        ['Avanzamento ricerca', `${battute} / ${totMiss} missioni battute`, `${pctAvz}%`],
      ],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      tableWidth: 190,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65, fillColor: [243, 244, 246] }, 2: { halign: 'center', cellWidth: 35, fontStyle: 'bold' } },
    });

    generatePDF.addFooter(doc, false);
    doc.save(`Riepilogo_${operazione.nome.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF Riepilogo generato!');
  },

  squadre: async (operazione, squadre, soccorritori, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ orientation: 'landscape' });
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'COMPOSIZIONE SQUADRE', logoBase64, true);
    const socc = (id) => soccorritori.find(s => s.id === id);
    const tableData = squadre.map((sq, i) => {
      const compNomi = (sq.componenti_ids || []).map(id => socc(id)?.cognome_nome || id).join(', ');
      const tlTel = socc(sq.team_leader_id)?.cellulare;
      const tlCell = (sq.team_leader_nome || '—') + (tlTel ? `\nTel. ${tlTel}` : '');
      // Stato coerente con la pagina: IN MISSIONE / NON DISPONIBILE (vuota o con un
      // componente PARTITO/impegnato altrove) / LIBERA.
      const isBusy = !!sq.missione_attuale_id;
      const compIds = Array.from(new Set([sq.team_leader_id, ...(sq.componenti_ids || [])].filter(Boolean)));
      const isNonDisp = !isBusy && (compIds.length === 0 || compIds.some(id => { const s = socc(id); return s && (s.stato === 'PARTITO' || s.impegnato); }));
      const stato = isBusy ? 'IN MISSIONE' : (isNonDisp ? 'NON DISPONIBILE' : 'LIBERA');
      return [
        i + 1,
        sq.sigla,
        sq.tipo,
        tlCell,
        compNomi || '—',
        sq.id_radio || '—',
        sq.freq_uhf || '—',
        stato,
      ];
    });
    autoTable(doc, {
      startY: y,
      head: [['N°', 'Sigla', 'Tipo', 'Team Leader', 'Componenti', 'Radio', 'UHF', 'Stato']],
      body: tableData,
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableWidth: 277,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: 22 }, 2: { cellWidth: 22 }, 3: { cellWidth: 48 }, 4: { cellWidth: 108 }, 5: { cellWidth: 24 }, 6: { cellWidth: 22 }, 7: { cellWidth: 19 } },
    });
    generatePDF.addFooter(doc, true);
    doc.save(`Squadre_${operazione.nome.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF Squadre generato!');
  },

  missioni: async (operazione, missioni, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ orientation: 'landscape' });
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'ZONE / LINEE / PUNTI DI RICERCA', logoBase64, true);
    const fmtD = (d) => { if (!d) return 'Senza data'; const p = String(d).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
    const groups = {};
    missioni.forEach(m => { const d = m.data_apertura || ''; (groups[d] = groups[d] || []).push(m); });
    // Gruppi per data di apertura, dal più recente; senza-data in fondo.
    const dates = Object.keys(groups).sort((a, b) => (!a ? 1 : !b ? -1 : b.localeCompare(a)));
    dates.forEach(d => {
      // Dentro il gruppo: dalla meno recente alla più recente (per ora di apertura).
      const list = groups[d].sort((a, b) => String(a.ora_apertura || '').localeCompare(String(b.ora_apertura || '')));
      if (y > 188) { doc.addPage(); y = 15; }
      doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
      doc.text(`Apertura: ${fmtD(d)}  (${list.length})`, 10, y); y += 2;
      autoTable(doc, {
        startY: y + 1,
        head: [['N°', 'Nome / Descrizione', 'Stato', 'N° Battute', 'Squadre', 'GPS', 'Radio', 'Ora Apertura', 'Chiusura']],
        body: list.map((m, i) => {
          const battute = (m.storico || []).filter(e => e.stato === 'BATTUTA');
          const squadreBattuto = [...new Set(battute.map(e => e.squadra_sigla).filter(Boolean))].join(', ') || '—';
          return [
            i + 1,
            m.nome || '—',
            m.stato || '—',
            battute.length,
            squadreBattuto,
            (Array.isArray(m.gps_lista) && m.gps_lista.length ? m.gps_lista.join(', ') : (m.gps_assegnati || '—')),
            (Array.isArray(m.radio_lista) && m.radio_lista.length ? m.radio_lista.join(', ') : (m.radio_assegnati || '—')),
            m.ora_apertura || '—',
            m.data_chiusura ? `${fmtD(m.data_chiusura)} ${m.ora_chiusura || ''}`.trim() : '—',
          ];
        }),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        tableWidth: 277, margin: { left: 10, right: 10 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 74 }, 2: { cellWidth: 26 }, 3: { cellWidth: 18, halign: 'center' }, 4: { cellWidth: 40 }, 5: { cellWidth: 18 }, 6: { cellWidth: 18 }, 7: { cellWidth: 24 }, 8: { cellWidth: 49 } },
      });
      y = doc.lastAutoTable.finalY + 6;
    });
    if (!dates.length) { doc.setFontSize(9); doc.setTextColor(120); doc.text('Nessuna missione.', 10, y); }
    generatePDF.addFooter(doc, true);
    doc.save(`Missioni_${operazione.nome.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF Zone/Missioni generato!');
  },

  // Dettaglio singola missione (campi + storico stati).
  missioneDettaglio: async (operazione, m, squadre, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    let y = generatePDF.addInstitutionalHeader(doc, operazione, `DETTAGLIO MISSIONE — ${m.nome || ''}`, logoBase64, false);
    const dev = (l, leg) => (Array.isArray(l) && l.length ? l.join(', ') : (leg || '—'));
    const coord = (m.lat != null && m.lon != null) ? `${Number(m.lat).toFixed(6)}, ${Number(m.lon).toFixed(6)}` : '—';
    autoTable(doc, {
      startY: y,
      body: [
        ['Missione', m.nome || '—'],
        ['Tipo', m.tipo || '—'],
        ['Stato', m.stato || '—'],
        ['Descrizione', m.descrizione || '—'],
        ['Note', m.note || '—'],
        ['Squadra', m.squadra_sigla || '—'],
        ['GPS', dev(m.gps_lista, m.gps_assegnati)],
        ['Radio', dev(m.radio_lista, m.radio_assegnati)],
        ['Apertura', m.data_apertura ? `${m.data_apertura} ${m.ora_apertura || ''}`.trim() : '—'],
        ['Chiusura', m.data_chiusura ? `${m.data_chiusura} ${m.ora_chiusura || ''}`.trim() : '—'],
        ['Coordinate', coord],
        ['Superficie', m.area_km2 != null ? `${(m.area_km2 * 100).toFixed(2)} ha` : '—'],
        ['Lunghezza', m.lunghezza_m != null ? `${Math.round(m.lunghezza_m)} m` : '—'],
      ],
      theme: 'grid', styles: { fontSize: 9, cellPadding: 2.5 }, tableWidth: 190, margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, fillColor: [243, 244, 246] } },
    });
    y = doc.lastAutoTable.finalY + 6;
    const st = (m.storico || []);
    if (st.length) {
      doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
      doc.text('Storico stati', 10, y); y += 2;
      autoTable(doc, {
        startY: y + 1,
        head: [['Stato', 'Data / Ora', 'Squadra', 'Operatore']],
        body: st.slice().reverse().map(e => [
          e.stato || '—',
          `${e.data || ''} ${e.ora || ''}`.trim() + (e.ora_fine ? ` → fine ${e.ora_fine}` : (e.ora_inizio ? ` · inizio ${e.ora_inizio}` : '')),
          e.squadra_sigla || '—',
          e.operatore || '—',
        ]),
        styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        tableWidth: 190, margin: { left: 10, right: 10 },
      });
    }
    generatePDF.addFooter(doc, false);
    doc.save(`Missione_${(m.nome || 'dettaglio').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF missione generato!');
  },

  // Dettaglio singola squadra (composizione + missioni assegnate/battute).
  squadraDettaglio: async (operazione, sq, soccorritori = [], missioni = [], logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    let y = generatePDF.addInstitutionalHeader(doc, operazione, `DETTAGLIO SQUADRA — ${sq.sigla || ''}`, logoBase64, false);
    const socc = (id) => soccorritori.find(s => s.id === id);
    const tlTel = socc(sq.team_leader_id)?.cellulare;
    autoTable(doc, {
      startY: y,
      body: [
        ['Sigla', sq.sigla || '—'],
        ['Tipo', sq.tipo || '—'],
        ['Team Leader', (sq.team_leader_nome || '—') + (tlTel ? `  (Tel. ${tlTel})` : '')],
        ['ID Radio', sq.id_radio || '—'],
        ['Freq. UHF', sq.freq_uhf || '—'],
        ['Freq. VHF', sq.freq_vhf || '—'],
        ['Stato', sq.missione_attuale_id ? 'IN MISSIONE' : 'LIBERA'],
      ],
      theme: 'grid', styles: { fontSize: 9, cellPadding: 2.5 }, tableWidth: 190, margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, fillColor: [243, 244, 246] } },
    });
    y = doc.lastAutoTable.finalY + 6;
    // Componenti
    doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
    doc.text('Componenti', 10, y); y += 2;
    const comp = (sq.componenti_ids || []).map((id, i) => {
      const s = socc(id);
      return [i + 1, s?.cognome_nome || id, s?.ente || '—', s?.specializzazione || '—', s?.cellulare || '—'];
    });
    autoTable(doc, {
      startY: y + 1,
      head: [['N°', 'Cognome Nome', 'Ente', 'Specializzazione', 'Cellulare']],
      body: comp.length ? comp : [['—', 'Nessun componente', '', '', '']],
      styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      tableWidth: 190, margin: { left: 10, right: 10 },
    });
    y = doc.lastAutoTable.finalY + 6;
    // Missioni battute da questa squadra (storico BATTUTA)
    const battute = missioni.filter(m => (m.storico || []).some(e => e.stato === 'BATTUTA' && (e.squadra_id === sq.id || e.squadra_sigla === sq.sigla)));
    if (battute.length) {
      if (y > 250) { doc.addPage(); y = 15; }
      doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
      doc.text('Missioni battute', 10, y); y += 2;
      autoTable(doc, {
        startY: y + 1,
        head: [['Missione', 'Tipo', 'Superficie / Lunghezza']],
        body: battute.map(m => [m.nome || '—', m.tipo || '—', m.area_km2 != null ? `${(m.area_km2 * 100).toFixed(2)} ha` : (m.lunghezza_m != null ? `${Math.round(m.lunghezza_m)} m` : '—')]),
        styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        tableWidth: 190, margin: { left: 10, right: 10 },
      });
    }
    generatePDF.addFooter(doc, false);
    doc.save(`Squadra_${(sq.sigla || 'dettaglio').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF squadra generato!');
  },

  comunicazioni: async (operazione, comunicazioni, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ orientation: 'landscape' });
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'CRONOLOGICO COMUNICAZIONI', logoBase64, true);
    const tableData = comunicazioni.map((c, i) => [
      i + 1,
      c.data || '—',
      c.ora || '—',
      c.tipo || '—',
      c.priorita || '—',
      c.mittente || '—',
      c.destinatario || '—',
      c.oggetto || '—',
      c.messaggio || '—',
    ]);
    autoTable(doc, {
      startY: y,
      head: [['N°', 'Data', 'Ora', 'Tipo', 'Priorità', 'Mittente', 'Destinatario', 'Oggetto', 'Messaggio']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableWidth: 277,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 24 }, 2: { cellWidth: 18 }, 3: { cellWidth: 22 }, 4: { cellWidth: 20 }, 5: { cellWidth: 34 }, 6: { cellWidth: 34 }, 7: { cellWidth: 46 }, 8: { cellWidth: 69 } },
    });
    generatePDF.addFooter(doc, true);
    doc.save(`Comunicazioni_${operazione.nome.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF Comunicazioni generato!');
  },
  
  logs: async (operazione, logs, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ orientation: 'landscape' });
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'LOG OPERAZIONI', logoBase64, true);
    const tableData = logs.map((l) => [
      l.timestamp?.split('T')[0] || '-',
      l.timestamp?.split('T')[1]?.substring(0, 8) || '-',
      l.tipo,
      l.operatore_email || 'Sistema',
      l.entita_tipo,
      l.descrizione
    ]);
    autoTable(doc, {
      startY: y,
      head: [['Data', 'Ora', 'Tipo', 'Operatore', 'Entità', 'Descrizione']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableWidth: 277,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 20 }, 2: { cellWidth: 25 }, 3: { cellWidth: 46 }, 4: { cellWidth: 32 }, 5: { cellWidth: 128 } },
    });
    generatePDF.addFooter(doc, true);
    doc.save(`Log_${operazione.nome.replace(/\s+/g, '_')}_${operazione.data_inizio}.pdf`);
    toast.success('PDF Log generato!');
  },
  
  soccorritori: async (operazione, soccorritori, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF({ orientation: 'landscape' });
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'ELENCO SOCCORRITORI', logoBase64, true);
    const fmtD = (d) => { if (!d) return 'Senza data'; const p = String(d).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
    // Una riga per TURNO di presenza: chi è arrivato più volte compare più volte,
    // nel rispettivo raggruppamento per data di arrivo (col proprio arrivo/partenza).
    const rows = [];
    soccorritori.forEach(s => {
      const turni = (Array.isArray(s.turni_presenza) && s.turni_presenza.length)
        ? s.turni_presenza
        : [{ data_arrivo: s.data_arrivo, ora_arrivo: s.ora_arrivo, data_partenza: s.data_partenza, ora_partenza: s.ora_partenza }];
      turni.forEach(t => rows.push({ s, t }));
    });
    const groups = {};
    rows.forEach(r => { const d = r.t.data_arrivo || ''; (groups[d] = groups[d] || []).push(r); });
    // Gruppi per data di arrivo, dal più recente; senza-data in fondo.
    const dates = Object.keys(groups).sort((a, b) => (!a ? 1 : !b ? -1 : b.localeCompare(a)));
    dates.forEach(d => {
      const list = groups[d].sort((a, b) => (a.s.cognome_nome || '').localeCompare(b.s.cognome_nome || '', 'it', { sensitivity: 'base' }));
      if (y > 188) { doc.addPage(); y = 15; }
      doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
      doc.text(`Arrivo: ${fmtD(d)}  (${list.length})`, 10, y); y += 2;
      autoTable(doc, {
        startY: y + 1,
        head: [['Cognome Nome (Grado)', 'Ente', 'Specializzazione', 'Provenienza', 'Cellulare', 'Ora Arrivo', 'Partenza']],
        body: list.map(({ s, t }) => [
          `${s.cognome_nome}${s.grado ? ` (${s.grado})` : ''}`,
          s.ente || '—',
          s.specializzazione || '—',
          s.provenienza || '—',
          s.cellulare || '—',
          t.ora_arrivo || '—',
          t.ora_partenza ? ((t.data_partenza && t.data_partenza !== d) ? `${fmtD(t.data_partenza)} ${t.ora_partenza}` : t.ora_partenza) : '—',
        ]),
        styles: { fontSize: 7.5, cellPadding: 2 },
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        tableWidth: 277, margin: { left: 10, right: 10 },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 45 }, 2: { cellWidth: 42 }, 3: { cellWidth: 40 }, 4: { cellWidth: 34 }, 5: { cellWidth: 22 }, 6: { cellWidth: 24 } },
      });
      y = doc.lastAutoTable.finalY + 6;
    });
    if (!dates.length) { doc.setFontSize(9); doc.setTextColor(120); doc.text('Nessun soccorritore.', 10, y); }
    generatePDF.addFooter(doc, true);
    doc.save(`Soccorritori_${operazione.nome.replace(/\s+/g, '_')}_${operazione.data_inizio}.pdf`);
    return true;
  },
  
  turno: async (operazione, soccorritori, squadre, missioni, turno, logoBase64) => {
    const { jsPDF, autoTable } = await loadPdf();
    const doc = new jsPDF();
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'RIEPILOGO TURNO', logoBase64, false);
    const infoTurno = [
      ['Turno', turno?.nome_turno || operazione.turno_tas || '—'],
      ['Data Inizio', operazione.data_inizio || '—'],
      ['Ora Inizio', operazione.ora_inizio || '—'],
      ['TAS2 #1', turno?.tas2_1_nome || '—'],
      ['TAS2 #2', turno?.tas2_2_nome || '—'],
      ['Funzionario di Servizio', turno?.funzionario_servizio || operazione.funzionario_servizio || '—'],
      ['Capo Turno', turno?.capo_turno || operazione.capo_turno || '—'],
    ];
    autoTable(doc, {
      startY: y,
      head: [['Campo', 'Valore']],
      body: infoTurno,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      tableWidth: 190,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65, fillColor: [243, 244, 246] } },
    });
    y = doc.lastAutoTable.finalY + 6;
    const daBattere = missioni.filter(m => m.stato === 'DA_BATTERE').length;
    const inBattuta = missioni.filter(m => m.stato === 'IN_BATTUTA').length;
    const battute = missioni.filter(m => m.stato === 'BATTUTA').length;
    const stats = [
      ['Soccorritori Attivi', soccorritori.filter(s => s.stato === 'ATTIVO').length.toString()],
      ['Squadre Operative', squadre.length.toString()],
      ['Missioni Da Battere', daBattere.toString()],
      ['Missioni In Battuta', inBattuta.toString()],
      ['Missioni Battute', battute.toString()],
    ];
    autoTable(doc, {
      startY: y,
      head: [['Statistica', 'Valore']],
      body: stats,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      tableWidth: 190,
      margin: { left: 10, right: 10 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65, fillColor: [243, 244, 246] } },
    });
    generatePDF.addFooter(doc, false);
    doc.save(`Turno_${operazione.nome.replace(/\s+/g, '_')}_${operazione.data_inizio}.pdf`);
    return true;
  },

  qrCode: async (operazione, logoBase64, qrImageData, qrUrl) => {
    const { jsPDF } = await loadPdf();
    const doc = new jsPDF();
    const W = 210, ML = 10, CW = W - 20;
    let y = generatePDF.addInstitutionalHeader(doc, operazione, 'QR CODE — REGISTRAZIONE SOCCORRITORI', logoBase64, false);

    y += 6;

    // QR code box centrato con bordo e sfondo bianco
    const qrSize = 80;
    const qrX = (W - qrSize) / 2;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(200, 200, 200);
    doc.rect(qrX - 8, y - 4, qrSize + 16, qrSize + 8, 'FD');
    doc.addImage(qrImageData, 'PNG', qrX, y, qrSize, qrSize);
    y += qrSize + 10;

    // Nome operazione
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 41, 59);
    doc.text(operazione.nome || '—', W / 2, y, { align: 'center' });
    y += 12;

    // Link "ISCRIVITI QUI"
    const linkLabel = 'REGISTRAZIONE SOCCORRITORI';
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 100, 200);
    const linkW = doc.getTextWidth(linkLabel);
    const linkX = (W - linkW) / 2;
    doc.textWithLink(linkLabel, linkX, y, { url: qrUrl });
    // Underline
    doc.setDrawColor(30, 100, 200);
    doc.line(linkX, y + 1, linkX + linkW, y + 1);
    y += 14;

    // Istruzioni
    doc.setFillColor(243, 244, 246);
    doc.rect(ML + 10, y - 3, CW - 20, 12, 'F');
    doc.setFontSize(8); doc.setFont(undefined, 'italic'); doc.setTextColor(60, 60, 60);
    doc.text('Scansiona il QR code con la fotocamera del telefono per registrarti come soccorritore a questa operazione.', W / 2, y + 5, { align: 'center', maxWidth: CW - 24 });

    generatePDF.addFooter(doc, false);
    doc.save(`QRCode_${(operazione.nome || 'operazione').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF QR Code generato!');
  },
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;
  const sizeClass = size === "lg" ? "max-w-4xl" : size === "sm" ? "max-w-md" : "max-w-2xl";
  return (
    <div className="modal-overlay" data-testid="modal-overlay">
      <div className={`modal-content ${sizeClass}`} data-testid="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="btn-ghost" onClick={onClose} data-testid="modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Login Page
const LoginPage = ({ onLogin }) => {
  const [isRegister, setIsRegister]           = useState(false);
  const [showReset, setShowReset]             = useState(false);
  const [showPassword, setShowPassword]       = useState(false);
  const [form, setForm]                       = useState({ email: "", password: "", confermaPassword: "", nome: "", cognome: "" });
  const [loading, setLoading]                 = useState(false);
  const [resetToken, setResetToken]           = useState("");
  // Stato dopo registrazione o login bloccato per email non verificata
  const [pendingVerification, setPendingVerification] = useState(false);
  const [pendingEmail, setPendingEmail]       = useState("");
  const [emailSent, setEmailSent]             = useState(true);
  const [resendLoading, setResendLoading]     = useState(false);
  const [resendDone, setResendDone]           = useState(false);
  // Tema / logo dall'endpoint pubblico. Inizializzati dalla cache locale così
  // che OFFLINE la pagina di login mostri comunque il logo/nome configurato in
  // admin (l'ultimo visto su questo dispositivo) invece del default anonimo.
  const cachedTema = (() => {
    try { return JSON.parse(localStorage.getItem("tas_tema_cache") || "null"); }
    catch { return null; }
  })();
  const [temaLogo, setTemaLogo]               = useState(cachedTema?.logo_base64 || null);
  const [temaNome, setTemaNome]               = useState(cachedTema?.nome_sistema || "TAS Command");
  const [temaReady, setTemaReady]             = useState(false);

  useEffect(() => {
    // Applica subito il tema in cache (colori/favicon) per evitare flash offline.
    if (cachedTema) applyTheme(cachedTema);
    axios.get(`${API}/impostazioni`).then(({ data }) => {
      if (data.logo_base64) setTemaLogo(data.logo_base64);
      if (data.nome_sistema) setTemaNome(data.nome_sistema);
      applyTheme(data);
      // Aggiorna la cache per gli usi offline successivi.
      try {
        localStorage.setItem("tas_tema_cache", JSON.stringify({
          logo_base64: data.logo_base64 || null,
          nome_sistema: data.nome_sistema || null,
          colore_primario: data.colore_primario,
          colore_secondario: data.colore_secondario,
          favicon_base64: data.favicon_base64,
        }));
      } catch { /* quota piena: ignora */ }
    }).catch(() => { /* offline: resta il tema in cache */ }).finally(() => setTemaReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.endsWith("@vigilfuoco.it")) {
      toast.error("Email deve essere del dominio @vigilfuoco.it");
      return;
    }
    if (isOffline()) {
      toast.error("📵 Sei offline — il login richiede connessione. Riprova quando sei online.");
      return;
    }
    if (isRegister && form.password !== form.confermaPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      const api = createApi(null);
      if (isRegister) {
        const { data } = await api.register(form);
        // La registrazione non restituisce più un token: richiede verifica email
        if (data.email_verification_required) {
          setPendingEmail(data.email);
          setEmailSent(data.email_sent !== false);
          setPendingVerification(true);
          if (data.email_sent !== false) {
            toast.success("Registrazione completata! Controlla la tua email.");
          } else {
            toast.warning("Registrazione completata, ma l'email non è stata inviata. Usa il pulsante per reinviarla.");
          }
        } else {
          // Verifica email disabilitata: accesso diretto dopo la registrazione
          localStorage.setItem("tas_token", data.access_token);
          localStorage.setItem("tas_operatore", JSON.stringify(data.operatore));
          // Flag one-shot: al primo accesso dopo la registrazione l'app reindirizza al profilo
          localStorage.setItem("tas_primo_accesso", "1");
          toast.success("Registrazione completata! Benvenuto.");
          onLogin(data.operatore, data.access_token);
        }
      } else {
        const { data } = await api.login({ email: form.email, password: form.password });
        localStorage.setItem("tas_token", data.access_token);
        localStorage.setItem("tas_operatore", JSON.stringify(data.operatore));
        onLogin(data.operatore, data.access_token);
        toast.success(`Bentornato ${data.operatore.nome}!`);
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = apiErr(error, "Errore di autenticazione");
      if (status === 403 && detail.toLowerCase().includes("verificat")) {
        // Login bloccato: email non verificata
        setPendingEmail(form.email);
        setPendingVerification(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendDone(false);
    try {
      const api = createApi(null);
      await api.resendVerification({ email: pendingEmail });
      setResendDone(true);
      toast.success("Email di verifica inviata! Controlla la tua casella (anche lo spam).");
    } catch {
      toast.error("Errore nell'invio. Riprova tra qualche istante.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!form.email.endsWith("@vigilfuoco.it")) {
      toast.error("Email deve essere del dominio @vigilfuoco.it");
      return;
    }
    setLoading(true);
    try {
      const api = createApi(null);
      // Il token di reset NON viene più restituito dall'API (era account-takeover):
      // arriva solo via email come link /reset-password?token=… (gestito da ResetPasswordPage).
      await api.resetPassword({ email: form.email });
      toast.success("Se l'email è registrata, riceverai il link per reimpostare la password. Controlla la posta (anche lo spam).");
      setShowReset(false);
    } catch (error) {
      toast.error(apiErr(error, "Errore"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    const policyErr = passwordPolicyError(form.password);
    if (policyErr) { toast.error(policyErr); return; }
    setLoading(true);
    try {
      const api = createApi(null);
      await api.resetPasswordConfirm({ token: resetToken, new_password: form.password });
      toast.success("Password aggiornata! Effettua il login.");
      setShowReset(false);
      setResetToken("");
    } catch (error) {
      toast.error(apiErr(error, "Errore"));
    } finally {
      setLoading(false);
    }
  };

  const logoBlock = (
    <div className="text-center mb-8">
      {!temaReady ? (
        /* Placeholder invisibile stessa altezza del logo: evita il flash scudo→logo */
        <div className="h-16 w-16 mx-auto mb-4" />
      ) : temaLogo ? (
        <img src={temaLogo} alt="Logo" className="h-16 w-auto mx-auto mb-4 object-contain" />
      ) : (
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl mb-4">
          <Shield size={32} className="text-white" />
        </div>
      )}
      <h1 className="text-3xl font-bold text-white">{temaNome}</h1>
      <p className="text-slate-400 mt-2">Sistema Gestione Ricerca Persona</p>
    </div>
  );

  // ── Schermata "verifica email in attesa" ──────────────────────────────────
  if (pendingVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
        <div className="w-full max-w-md">
          {logoBlock}
          <div className="tactical-card p-6 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-500/10 rounded-full mb-4 border border-orange-500/30">
              <Mail size={28} className="text-orange-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Verifica il tuo account</h2>
            <p className="text-slate-400 mb-1 text-sm">
              {emailSent ? "Abbiamo inviato un link di verifica a:" : "Registrazione completata. Il link di verifica verrà inviato a:"}
            </p>
            <p className="text-orange-400 font-mono text-sm mb-4">{pendingEmail}</p>

            {!emailSent && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
                <p className="text-yellow-400 text-xs font-semibold mb-1">⚠ Email non recapitata</p>
                <p className="text-slate-400 text-xs">
                  Il sistema non è riuscito a inviare l'email in questo momento.
                  Usa il pulsante qui sotto per riprovare, oppure contatta l'amministratore.
                </p>
              </div>
            )}

            <p className="text-slate-500 text-xs mb-6">
              {emailSent
                ? <>Clicca il link nell'email per attivare il tuo account.<br />Il link è valido per 24 ore. Controlla anche la cartella spam.</>
                : <>Una volta ricevuta l'email, clicca il link per attivare l'account.<br />Il link sarà valido per 24 ore.</>
              }
            </p>

            {resendDone ? (
              <div className="flex items-center justify-center gap-2 text-green-400 text-sm mb-4">
                <CheckCircle size={16} /> Email inviata di nuovo!
              </div>
            ) : (
              <button
                className="btn btn-secondary w-full mb-3"
                onClick={handleResend}
                disabled={resendLoading}
              >
                {resendLoading ? (
                  <><RefreshCw size={14} className="animate-spin" /> Invio...</>
                ) : (
                  <><RefreshCw size={14} /> Reinvia email di verifica</>
                )}
              </button>
            )}

            <button
              className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
              onClick={() => { setPendingVerification(false); setResendDone(false); }}
            >
              ← Torna al login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md">
        {logoBlock}

        <div className="tactical-card p-6">
          {showReset ? (
            <form onSubmit={resetToken ? handleConfirmReset : handleResetPassword}>
              <h2 className="text-xl font-bold text-white mb-4">Recupero Password</h2>
              {!resetToken ? (
                <div className="form-group">
                  <label className="form-label">Email @vigilfuoco.it</label>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Indirizzo email" required data-testid="input-reset-email" />
                </div>
              ) : (
                <>
                  <div className="alert alert-success mb-4" style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.3)', color: '#10b981' }}>
                    Token: <code className="font-mono text-xs">{resetToken.substring(0, 20)}...</code>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nuova Password</label>
                    <div className="relative">
                      <input type={showPassword ? "text" : "password"} className="form-input pr-10" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={8} data-testid="input-new-password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </>
              )}
              <button type="submit" className="btn btn-primary w-full mt-4" disabled={loading}>
                {loading ? "Attendere..." : (resetToken ? "Conferma Nuova Password" : "Invia Reset")}
              </button>
              <button type="button" className="btn btn-secondary w-full mt-2" onClick={() => { setShowReset(false); setResetToken(""); }}>Torna al Login</button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-bold text-white mb-4">{isRegister ? "Registrazione" : "Accesso"}</h2>

              {isRegister && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Nome</label>
                    <input type="text" className="form-input" value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} required data-testid="input-nome" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cognome</label>
                    <input type="text" className="form-input" value={form.cognome} onChange={(e) => setForm({...form, cognome: e.target.value})} required data-testid="input-cognome" />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Email @vigilfuoco.it</label>
                <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Indirizzo email" required data-testid="input-email" />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} className="form-input pr-10" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={8} data-testid="input-password" />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {isRegister && (
                  <div className="mt-1.5 space-y-1">
                    <div className={`flex items-center gap-1.5 text-xs ${form.password.length >= 8 ? "text-green-400" : "text-slate-500"}`}>
                      <span>{form.password.length >= 8 ? "✓" : "○"}</span>
                      <span>Almeno 8 caratteri</span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs ${/\d/.test(form.password) ? "text-green-400" : "text-slate-500"}`}>
                      <span>{/\d/.test(form.password) ? "✓" : "○"}</span>
                      <span>Almeno un numero</span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs ${/[A-Z]/.test(form.password) ? "text-green-400" : "text-slate-500"}`}>
                      <span>{/[A-Z]/.test(form.password) ? "✓" : "○"}</span>
                      <span>Almeno una lettera maiuscola</span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs ${/[^A-Za-z0-9]/.test(form.password) ? "text-green-400" : "text-slate-500"}`}>
                      <span>{/[^A-Za-z0-9]/.test(form.password) ? "✓" : "○"}</span>
                      <span>Almeno un carattere speciale</span>
                    </div>
                  </div>
                )}
              </div>

              {isRegister && (
                <div className="form-group">
                  <label className="form-label">Conferma Password</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} className="form-input pr-10" value={form.confermaPassword} onChange={(e) => setForm({...form, confermaPassword: e.target.value})} required data-testid="input-conferma-password" />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {form.confermaPassword && form.password !== form.confermaPassword && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-400">
                      <span>✕</span><span>Le password non coincidono</span>
                    </div>
                  )}
                  {form.confermaPassword && form.password === form.confermaPassword && form.confermaPassword.length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-green-400">
                      <span>✓</span><span>Password corrispondenti</span>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="btn btn-primary w-full mt-4" disabled={loading} data-testid="btn-submit">
                <LogIn size={16} />
                {loading ? "Attendere..." : (isRegister ? "Registrati" : "Accedi")}
              </button>

              <div className="mt-4 text-center space-y-2">
                <button type="button" className="text-sm text-orange-500 hover:underline" onClick={() => setIsRegister(!isRegister)}>
                  {isRegister ? "Hai già un account? Accedi" : "Non hai un account? Registrati"}
                </button>
                <br />
                <button type="button" className="text-sm text-slate-400 hover:text-white" onClick={() => setShowReset(true)}>Password dimenticata?</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Auto-Register Page (Public)
// Distanza in metri tra due punti {lat,lon} (haversine). Usata per il throttle del
// tracking (non bufferizzare fix quasi-fermi).
const _haversineM = (a, b) => {
  const R = 6371000, toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const TRACK_LS_KEY = (opId) => `tas_tracking_${opId}`;

// Ripulisce TUTTO quello che questo device tiene su una registrazione che non
// esiste più: sessione, buffer dei fix, SOS in coda, flag di condivisione. Se ne
// restasse anche uno, alla registrazione successiva il telefono proverebbe a
// spedire i fix di un soccorritore cancellato.
const pulisciSessioneTracking = (opId, soccId) => {
  const chiavi = [TRACK_LS_KEY(opId)];
  if (soccId) chiavi.push(`tas_sos_${soccId}`, `tas_track_buf_${soccId}`, `tas_track_on_${soccId}`);
  chiavi.forEach(k => { try { localStorage.removeItem(k); } catch { /* privacy mode */ } });
};

// ── Cruscotto tracking del soccorritore (opzione B) ──────────────────────────
// Mostrato dopo la registrazione QR (o alla riapertura, da localStorage). Il
// consenso è esplicito e revocabile: il toggle ON attiva watchPosition + invio,
// OFF ferma tutto. I fix vanno in un buffer localStorage e vengono flushati al
// server; se offline restano in coda e partono alla riconnessione (dedup per ts
// lato server). Contesto pubblico: usa il tracking token, non il JWT operatore.
const TrackingDashboard = ({ operazione, session, logoBase64, nomeSistema }) => {
  const { soccorritore_id, tracking_token, nome } = session;
  // PENDING = registrato ma non ancora convalidato dalla sala operativa.
  // Si ricontrolla da solo: chi e' gia' in campo non deve riscansionare il QR per
  // accorgersi che nel frattempo l'hanno messo in elenco.
  const [statoReg, setStatoReg] = useState(session.stato || "");
  const inAttesa = statoReg === "PENDING";
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [pending, setPending] = useState(0);
  const [geoError, setGeoError] = useState(null);
  const [sosActive, setSosActive] = useState(false);
  const [sosBusy, setSosBusy] = useState(false);
  const [, setTick] = useState(0); // re-render per il "ultimo invio X fa"

  const SOS_KEY = `tas_sos_${soccorritore_id}`;

  const watchId = useRef(null);
  const wakeLock = useRef(null);
  const flushTimer = useRef(null);
  const bufRef = useRef([]);
  const lastBufRef = useRef(null);

  const BUF_KEY = `tas_track_buf_${soccorritore_id}`;
  // Flag locale "condivisione attiva": permette di riprendere il watchPosition dopo
  // un reload della pagina. Senza, il server resterebbe con tracking_attivo=True ma
  // il device non invierebbe più nulla (marker stantio in sala operativa).
  const ON_KEY = `tas_track_on_${soccorritore_id}`;
  const MIN_INTERVAL = 25000; // ms tra due fix bufferizzati
  const MIN_DIST = 15;        // m di spostamento per bufferizzare prima dell'intervallo
  const BUF_CAP = 500;        // punti max in buffer
  const MAX_AGE = 6 * 3600 * 1000; // scarta fix più vecchi di 6h

  const persistBuf = useCallback(() => {
    try { localStorage.setItem(BUF_KEY, JSON.stringify(bufRef.current)); } catch { /* quota */ }
    setPending(bufRef.current.length);
  }, [BUF_KEY]);

  const pruneBuf = useCallback(() => {
    const cutoff = Date.now() - MAX_AGE;
    bufRef.current = bufRef.current.filter(f => new Date(f.ts).getTime() >= cutoff);
    if (bufRef.current.length > BUF_CAP) bufRef.current = bufRef.current.slice(-BUF_CAP);
  }, [MAX_AGE, BUF_CAP]);

  const flush = useCallback(async () => {
    if (isOffline() || !bufRef.current.length) return;
    const batch = bufRef.current.slice(0, 200);
    try {
      await createApi(null).postPosizioni(soccorritore_id, tracking_token, batch);
      const sent = new Set(batch.map(f => f.ts));
      bufRef.current = bufRef.current.filter(f => !sent.has(f.ts));
      persistBuf();
      setLastSentAt(Date.now());
    } catch { /* resta in buffer, ritenta al prossimo giro/online */ }
  }, [soccorritore_id, tracking_token, persistBuf]);

  const onPosition = useCallback((pos) => {
    setGeoError(null);
    const { latitude, longitude, accuracy } = pos.coords;
    const now = Date.now();
    const last = lastBufRef.current;
    const far = !last || _haversineM(last, { lat: latitude, lon: longitude }) >= MIN_DIST;
    const old = !last || (now - new Date(last.ts).getTime()) >= MIN_INTERVAL;
    if (!(far || old)) return; // throttle
    const fix = { lat: latitude, lon: longitude, accuracy: accuracy ?? null, ts: new Date(now).toISOString() };
    bufRef.current.push(fix);
    lastBufRef.current = fix;
    pruneBuf();
    persistBuf();
    flush();
  }, [flush, pruneBuf, persistBuf, MIN_DIST, MIN_INTERVAL]);

  const acquireWakeLock = useCallback(async () => {
    try { if ('wakeLock' in navigator) wakeLock.current = await navigator.wakeLock.request('screen'); } catch { /* batteria/non supportato */ }
  }, []);
  const releaseWakeLock = useCallback(async () => {
    try { await wakeLock.current?.release(); } catch { /* già rilasciato */ }
    wakeLock.current = null;
  }, []);

  const startSharing = useCallback(async () => {
    if (!('geolocation' in navigator)) { setGeoError("Geolocalizzazione non supportata da questo dispositivo"); return; }
    setBusy(true);
    try {
      await createApi(null).setTrackingConsenso(soccorritore_id, tracking_token, true);
      setSharing(true);
      try { localStorage.setItem(ON_KEY, '1'); } catch { /* quota */ }
      await acquireWakeLock();
      watchId.current = navigator.geolocation.watchPosition(
        onPosition,
        (err) => setGeoError(err && err.code === 1 ? "Permesso posizione negato" : "Impossibile ottenere la posizione"),
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 },
      );
      flushTimer.current = setInterval(flush, 20000);
    } catch (e) {
      toast.error(apiErr(e, "Impossibile attivare la condivisione"));
    } finally { setBusy(false); }
  }, [soccorritore_id, tracking_token, onPosition, flush, acquireWakeLock, ON_KEY]);

  const stopSharing = useCallback(async () => {
    setBusy(true);
    if (watchId.current != null) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    if (flushTimer.current) { clearInterval(flushTimer.current); flushTimer.current = null; }
    await releaseWakeLock();
    setSharing(false);
    try { localStorage.removeItem(ON_KEY); } catch { /* noop */ }
    try { await createApi(null).setTrackingConsenso(soccorritore_id, tracking_token, false); } catch { /* best effort */ }
    setBusy(false);
  }, [soccorritore_id, tracking_token, releaseWakeLock, ON_KEY]);

  // ── SOS: allarme con posizione alla sala operativa. Funziona anche con tracking OFF
  //    (il click è l'atto esplicito d'emergenza). Se offline, l'SOS resta in coda
  //    (localStorage) e parte alla riconnessione.
  const getOnePosition = () => new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('no-geo'));
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
  });

  const flushSos = useCallback(async () => {
    if (isOffline()) return;
    let pend = null;
    try { pend = JSON.parse(localStorage.getItem(SOS_KEY) || 'null'); } catch { /* noop */ }
    if (!pend) return;
    try { await createApi(null).sosSoccorritore(soccorritore_id, tracking_token, pend); localStorage.removeItem(SOS_KEY); } catch { /* ritenta dopo */ }
  }, [soccorritore_id, tracking_token, SOS_KEY]);

  const handleSos = useCallback(async () => {
    if (sosBusy) return;
    if (!window.confirm("Inviare un SOS con la tua posizione alla sala operativa?")) return;
    setSosBusy(true);
    let fix = null;
    try {
      const pos = await getOnePosition();
      fix = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy ?? null, ts: new Date().toISOString() };
    } catch {
      const lb = lastBufRef.current; // fallback all'ultimo fix noto se il GPS non risponde
      if (lb) fix = { lat: lb.lat, lon: lb.lon, accuracy: lb.accuracy ?? null, ts: new Date().toISOString() };
    }
    if (!fix) { toast.error("Impossibile ottenere la posizione per l'SOS"); setSosBusy(false); return; }
    try {
      await createApi(null).sosSoccorritore(soccorritore_id, tracking_token, fix);
      try { localStorage.removeItem(SOS_KEY); } catch { /* noop */ }
      setSosActive(true);
      toast.success("SOS inviato alla sala operativa");
    } catch {
      try { localStorage.setItem(SOS_KEY, JSON.stringify(fix)); } catch { /* quota */ }
      setSosActive(true);
      toast.warning("SOS in invio: partirà appena torna la rete");
    } finally { setSosBusy(false); }
  }, [sosBusy, soccorritore_id, tracking_token, SOS_KEY]);

  const handleCancelSos = useCallback(async () => {
    setSosBusy(true);
    try { localStorage.removeItem(SOS_KEY); } catch { /* noop */ }
    try { await createApi(null).annullaSos(soccorritore_id, tracking_token); } catch { /* best effort */ }
    setSosActive(false);
    setSosBusy(false);
    toast.info("SOS annullato");
  }, [soccorritore_id, tracking_token, SOS_KEY]);

  // Carica il buffer salvato (es. riapertura pagina con fix non ancora inviati) +
  // eventuale SOS rimasto in coda (riapertura/offline): mostra stato attivo e ritenta.
  useEffect(() => {
    try { bufRef.current = JSON.parse(localStorage.getItem(BUF_KEY) || "[]"); } catch { bufRef.current = []; }
    setPending(bufRef.current.length);
    if (bufRef.current.length) flush(); // fix rimasti dal giro precedente: non aspettare una transizione di rete
    let pendSos = null;
    try { pendSos = localStorage.getItem(SOS_KEY); } catch { /* noop */ }
    if (pendSos) { setSosActive(true); flushSos(); }
  }, [BUF_KEY, SOS_KEY, flush, flushSos]);

  // Riapertura pagina con condivisione mai revocata → riprendi automaticamente
  // watchPosition + invio (il consenso resta quello già prestato; OFF lo revoca).
  const autoResumed = useRef(false);
  useEffect(() => {
    if (autoResumed.current) return;
    autoResumed.current = true;
    let on = null;
    try { on = localStorage.getItem(ON_KEY); } catch { /* noop */ }
    if (on === '1') startSharing();
  }, [ON_KEY, startSharing]);

  // online → flush; ritorno in foreground → ri-acquisisci il wake lock (si rilascia
  // da solo quando la pagina va in background).
  useEffect(() => {
    // Ripristino connettività (probe attivo): svuota il buffer dei fix + SOS in coda.
    const unsub = subscribeConnettivita((online) => { if (online) { flush(); flushSos(); } });
    const onVis = () => { if (document.visibilityState === "visible" && sharing) acquireWakeLock(); };
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(() => setTick(x => x + 1), 5000);
    return () => {
      unsub();
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(t);
    };
  }, [flush, flushSos, sharing, acquireWakeLock]);

  // Cleanup all'uscita.
  useEffect(() => () => {
    if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
    if (flushTimer.current) clearInterval(flushTimer.current);
    releaseWakeLock();
  }, [releaseWakeLock]);

  // Finche' e' in attesa: ricontrollo ogni minuto e al ritorno in primo piano.
  // A convalida avvenuta il polling si spegne da solo (la dipendenza diventa false).
  useEffect(() => {
    if (!inAttesa) return undefined;
    let vivo = true;
    const controlla = async () => {
      try {
        const { data } = await createApi(null).getTrackingStato(soccorritore_id, tracking_token);
        if (vivo && data && data.stato) setStatoReg(data.stato);
      } catch { /* offline o eliminato: se ne accorge la pagina al prossimo avvio */ }
    };
    const id = setInterval(controlla, 60000);
    const onFocus = () => { if (document.visibilityState === "visible") controlla(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      vivo = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [inAttesa, soccorritore_id, tracking_token]);

  const agoLabel = lastSentAt ? `${Math.max(0, Math.round((Date.now() - lastSentAt) / 1000))}s fa` : "—";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="tactical-card p-8 text-center max-w-md w-full">
        {logoBase64
          ? <img src={logoBase64} alt={nomeSistema} className="inline-block w-12 h-12 object-contain rounded-xl mb-3" />
          : <CheckCircle className="mx-auto mb-4 text-green-500" size={44} />}
        <h2 className="text-xl font-bold text-white mb-1">Sei registrato</h2>
        <p className="text-slate-400 mb-4 text-sm">{nome ? `${nome} · ` : ""}{operazione.nome}</p>

        {/* Registrato non vuol dire ancora operativo: finche' la sala operativa non
            convalida, la persona non e' in elenco. Dirlo qui evita che se ne accorga
            solo premendo SOS in un momento sbagliato. */}
        {inAttesa ? (
          <div className="rounded-lg border border-amber-600 bg-amber-950/30 p-3 mb-4 text-left">
            <p className="text-amber-300 font-semibold text-sm mb-1">⏳ In attesa di convalida</p>
            <p className="text-[12px] text-amber-200/80">
              I tuoi dati sono arrivati alla sala operativa. Un operatore deve confermarli
              prima che tu risulti in elenco: questa pagina si aggiorna da sola.
            </p>
          </div>
        ) : statoReg ? (
          <div className="rounded-lg border border-green-700 bg-green-950/25 p-2 mb-4">
            <p className="text-green-300 text-sm font-semibold">✓ Registrazione convalidata</p>
          </div>
        ) : null}

        <div className="p-4 bg-slate-900 rounded-lg mb-4 text-left">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MapPin size={18} className={sharing ? "text-green-400" : "text-slate-500"} />
              <span className="text-sm font-semibold text-white">Condividi posizione</span>
            </div>
            <button
              type="button"
              onClick={sharing ? stopSharing : startSharing}
              disabled={busy}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${sharing ? "bg-green-500" : "bg-slate-600"} ${busy ? "opacity-60" : ""}`}
              aria-pressed={sharing}
              data-testid="toggle-tracking"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${sharing ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {sharing
              ? "La tua posizione è condivisa con la sala operativa. Tieni questa pagina aperta."
              : "Disattivata. Attivando, autorizzi l'invio della tua posizione GPS durante la battuta."}
          </p>
        </div>

        {sharing && (
          <div className="text-xs text-slate-400 flex items-center justify-center gap-2 mb-2">
            <Clock size={13} /> Ultimo invio: {agoLabel}
            {pending > 0 && <span className="text-amber-400">· {pending} in coda</span>}
          </div>
        )}
        {geoError && <p className="text-xs text-red-400 mt-1">{geoError}</p>}

        {/* SOS: invia un allarme con la posizione. Funziona anche con tracking OFF. */}
        <div className="mt-4 pt-4 border-t border-slate-800">
          {!sosActive ? (
            <button
              type="button"
              onClick={handleSos}
              disabled={sosBusy || inAttesa}
              title={inAttesa ? "Disponibile dopo la convalida della registrazione" : ""}
              className={`w-full py-4 rounded-lg font-extrabold text-lg tracking-wide text-white transition ${(sosBusy || inAttesa) ? "bg-red-900 opacity-60 cursor-not-allowed" : "bg-red-600 hover:bg-red-500"}`}
              data-testid="btn-sos"
            >
              🆘 {sosBusy ? "Invio SOS…" : "INVIA SOS"}
            </button>
          ) : (
            <div className="rounded-lg border border-red-600 bg-red-950/40 p-4">
              <p className="text-red-300 font-bold mb-2">🆘 SOS inviato alla sala operativa</p>
              <button
                type="button"
                onClick={handleCancelSos}
                disabled={sosBusy}
                className="w-full py-2 rounded-lg border border-red-500 text-red-300 hover:bg-red-900/40 text-sm font-semibold transition"
              >
                {sosBusy ? "Annullamento…" : "Annulla SOS (falso allarme)"}
              </button>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">
            {inAttesa
              ? "L'SOS si attiva quando la sala operativa convalida la tua registrazione: prima di allora non risulti in elenco."
              : "L'SOS invia la tua posizione alla sala operativa anche se la condivisione è disattivata."}
          </p>
        </div>

        <p className="text-[11px] text-slate-600 mt-3">
          Puoi interrompere la condivisione in qualsiasi momento. I dati di posizione
          vengono cancellati alla chiusura dell'operazione.
        </p>
      </div>
    </div>
  );
};

// Riferimento stabile (module-level) per la guardia anti-injection della select
// Regione in registrazione QR: evita il remount dell'observer a ogni render.
const AUTO_REGIONE_OPTIONS_ATTESE = ["Seleziona...", ...Object.keys(REGIONI_PROVINCE)];

const AutoRegisterPage = () => {
  const [operazione, setOperazione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [trackingSession, setTrackingSession] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [nomeSistema, setNomeSistema] = useState("TAS Command");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [form, setForm] = useState({
    cognome: "", nome: "", grado: "", ente: "", specializzazione: "", regione: "", provenienza: "", cellulare: ""
  });

  // Guardia anti-injection (adware/estensioni che riscrivono il DOM su alcuni
  // Android, es. Campania→Amalfi). Ripristina le label delle <option> se alterate.
  // Regione: array stabile. Provincia: dipende dalla regione scelta → useMemo.
  const regioneSelectRef = useRef(null);
  const provinciaSelectRef = useRef(null);
  const provinciaOptionsAttese = useMemo(
    () => [
      form.regione ? "Seleziona..." : "Scegli prima la regione",
      ...(REGIONI_PROVINCE[form.regione] || []),
    ],
    [form.regione]
  );
  useSelectOptionsGuard(regioneSelectRef, AUTO_REGIONE_OPTIONS_ATTESE);
  useSelectOptionsGuard(provinciaSelectRef, provinciaOptionsAttese);

  const token = window.location.pathname.split('/register/')[1];

  // ── Carica le impostazioni grafiche pubbliche (logo + nome_sistema) ─────────
  // Endpoint pubblico /api/impostazioni — no auth richiesta.
  useEffect(() => {
    axios.get(`${API}/impostazioni`)
      .then(({ data }) => {
        if (data?.logo_base64) setLogoBase64(data.logo_base64);
        if (data?.nome_sistema) setNomeSistema(data.nome_sistema);
      })
      .catch(() => {}); // Fallback silenzioso a Shield icon + "TAS Command"
  }, []);

  useEffect(() => {
    const loadOperazione = async () => {
      try {
        const api = createApi(null);
        const { data } = await api.getOperazioneByToken(token);
        setOperazione(data);
        // Riapertura: se questo device si è già registrato per l'operazione, mostra
        // direttamente il cruscotto tracking (non il form → niente doppia iscrizione).
        // Ma la sessione salvata va VERIFICATA: se l'operatore ha eliminato il
        // soccorritore dall'elenco, questo device si riaprirebbe sul cruscotto di
        // una registrazione che non esiste più — e la persona resterebbe convinta
        // di essere iscritta senza poter rifare la registrazione.
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(TRACK_LS_KEY(data.id)) || "null"); }
        catch { saved = null; }
        if (saved && saved.tracking_token && saved.soccorritore_id) {
          try {
            const { data: st } = await createApi(null).getTrackingStato(saved.soccorritore_id, saved.tracking_token);
            setTrackingSession({ ...saved, nome: st.nome || saved.nome, stato: st.stato });
          } catch (err) {
            const code = err?.response?.status;
            if (code === 404 || code === 401 || code === 403) {
              // Non è più in elenco (o il token non vale più): si riparte dal form.
              pulisciSessioneTracking(data.id, saved.soccorritore_id);
              toast.info("La tua registrazione precedente non risulta più: compila di nuovo il modulo.", { duration: 8000 });
            } else {
              // Rete assente o server giù: NON si cancella niente e si tiene la
              // sessione. Chi è in campo senza campo deve poter usare la pagina.
              setTrackingSession(saved);
            }
          }
        }
      } catch (error) {
        toast.error(apiErr(error, "Operazione non trovata o link non valido"));
      } finally {
        setLoading(false);
      }
    };
    if (token) loadOperazione();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // COGNOME e NOME separati, normalizzati e ricomposti nel singolo cognome_nome
    // atteso dal backend (cognome MAIUSCOLO, nome capitalizzato).
    const cognomeNome = `${fmtCognome(form.cognome).trim()} ${fmtNome(form.nome).trim()}`.trim();
    if (!cognomeNome || !form.ente || !form.specializzazione || !form.cellulare.trim()) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    if (!privacyAccepted) {
      toast.error("Devi accettare l'informativa privacy per procedere");
      return;
    }
    try {
      const api = createApi(null);
      const { data } = await api.autoRegisterSoccorritore({ ...form, cognome_nome: cognomeNome, operazione_id: operazione.id });
      // Salva la sessione tracking (token capability) su questo device → cruscotto
      // tracking + riapertura. Il tracking parte solo se il soccorritore accende il toggle.
      if (data && data.id && data.tracking_token) {
        const sess = { soccorritore_id: data.id, tracking_token: data.tracking_token, nome: data.cognome_nome, stato: data.stato };
        try { localStorage.setItem(TRACK_LS_KEY(operazione.id), JSON.stringify(sess)); } catch { /* quota */ }
        setTrackingSession(sess);
      }
      setSubmitted(true);
      toast.success("Registrazione completata!");
    } catch (error) {
      toast.error(apiErr(error, "Errore nella registrazione"));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><div className="text-white">Caricamento...</div></div>;
  
  if (!operazione) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="tactical-card p-8 text-center max-w-md">
        <AlertTriangle className="mx-auto mb-4 text-red-500" size={48} />
        <h2 className="text-xl font-bold text-white mb-2">Link Non Valido</h2>
        <p className="text-slate-400">Il link di registrazione non è valido o l'operazione non esiste.</p>
      </div>
    </div>
  );

  // Registrato su questo device (post-submit o riapertura) → cruscotto tracking.
  if (trackingSession) return (
    <TrackingDashboard operazione={operazione} session={trackingSession} logoBase64={logoBase64} nomeSistema={nomeSistema} />
  );

  // Fallback: registrazione completata ma senza sessione tracking (token assente).
  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="tactical-card p-8 text-center max-w-md">
        <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
        <h2 className="text-xl font-bold text-white mb-2">Registrazione Completata!</h2>
        <p className="text-slate-400 mb-4">I tuoi dati sono stati inviati. Un operatore ti assegnerà a breve a una squadra.</p>
        <div className="p-4 bg-slate-900 rounded-lg">
          <p className="text-sm text-slate-500">Operazione:</p>
          <p className="font-bold text-white">{operazione.nome}</p>
          <p className="text-sm text-slate-400">{operazione.localita}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 p-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          {logoBase64 ? (
            <img
              src={logoBase64}
              alt={nomeSistema}
              className="inline-block w-14 h-14 object-contain rounded-xl mb-3"
            />
          ) : (
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl mb-3">
              <Shield size={24} className="text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">Registrazione Soccorritore</h1>
          <p className="text-xs text-slate-500 mt-1">{nomeSistema}</p>
        </div>
        
        <div className="tactical-card p-4 mb-4">
          <p className="text-sm text-slate-500">Operazione:</p>
          <p className="font-bold text-white">{operazione.nome}</p>
          <p className="text-sm text-slate-400">{operazione.localita} - {operazione.data_inizio}</p>
        </div>

        <form onSubmit={handleSubmit} className="tactical-card p-6">
          <div className="form-group">
            <label className="form-label">Cognome *</label>
            <input type="text" className="form-input" style={{ textTransform: 'uppercase' }} value={form.cognome} onChange={(e) => setForm({...form, cognome: fmtCognome(e.target.value)})} placeholder="es. ROSSI" required data-testid="input-auto-cognome" />
          </div>
          <div className="form-group">
            <label className="form-label">Nome *</label>
            <input type="text" className="form-input" value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} onBlur={(e) => setForm({...form, nome: fmtNome(e.target.value)})} placeholder="es. Mario" required data-testid="input-auto-nome" />
          </div>
          <div className="form-group">
            <label className="form-label">Grado (opzionale)</label>
            <input type="text" className="form-input" value={form.grado} onChange={(e) => setForm({...form, grado: e.target.value})} data-testid="input-auto-grado" />
          </div>
          <div className="form-group">
            <label className="form-label">Ente/Amministrazione *</label>
            <select className="form-select" value={form.ente} onChange={(e) => setForm({...form, ente: e.target.value})} required data-testid="select-auto-ente">
              <option value="">Seleziona...</option>
              {ENTI.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Specializzazione *</label>
            <select className="form-select" value={form.specializzazione} onChange={(e) => setForm({...form, specializzazione: e.target.value})} required data-testid="select-auto-spec">
              <option value="">Seleziona...</option>
              {SPECIALIZZAZIONI.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Regione di provenienza</label>
            <select ref={regioneSelectRef} className="form-select" value={form.regione} onChange={(e) => setForm({...form, regione: e.target.value, provenienza: ""})} data-testid="select-auto-regione">
              <option value="">Seleziona...</option>
              {Object.keys(REGIONI_PROVINCE).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Provincia di provenienza</label>
            <select ref={provinciaSelectRef} className="form-select" value={form.provenienza} onChange={(e) => setForm({...form, provenienza: e.target.value})} disabled={!form.regione} data-testid="select-auto-prov">
              <option value="">{form.regione ? "Seleziona..." : "Scegli prima la regione"}</option>
              {(REGIONI_PROVINCE[form.regione] || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Cellulare *</label>
            <input type="tel" className="form-input font-mono" value={form.cellulare} onChange={(e) => setForm({...form, cellulare: e.target.value})} required data-testid="input-auto-cell" />
          </div>
          <div className="mt-5 p-4 bg-slate-900 rounded-lg border border-slate-700">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 flex-shrink-0 accent-orange-500 w-4 h-4"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                data-testid="checkbox-privacy"
              />
              <span className="text-sm text-slate-300 leading-relaxed">
                Ho letto e accetto l'
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 underline hover:text-orange-300 font-medium"
                >
                  Informativa sulla Privacy
                </a>
                {" "}ai sensi del Regolamento UE 2016/679 (GDPR) relativa al trattamento
                dei miei dati personali nell'ambito dell'applicazione TAS Command.
              </span>
            </label>
          </div>
          <button
            type="submit"
            className="btn btn-primary w-full mt-4"
            disabled={!privacyAccepted}
            data-testid="btn-auto-register"
          >
            <UserCheck size={16} />
            Registrati
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Privacy Policy Page (Pubblica) ───────────────────────────────────────────
const PrivacyPage = () => (
  <div className="min-h-screen bg-slate-950 p-4 py-10">
    <div className="max-w-2xl mx-auto">
      <IntestazioneTema piccolo sottotitolo="Informativa sulla Privacy" />

      <div className="tactical-card p-6 space-y-6 text-sm text-slate-300 leading-relaxed">

        <div>
          <h2 className="text-base font-bold text-white mb-2">Informativa Privacy</h2>
          <p className="text-xs text-slate-500">ai sensi del Regolamento UE 2016/679 (GDPR)</p>
        </div>

        <section>
          <h3 className="font-semibold text-white mb-2">Dati personali raccolti</h3>
          <p className="mb-2">La presente informativa riguarda esclusivamente i dati dei soccorritori che si registrano all'applicazione <strong className="text-white">"TAS Command"</strong> tramite QR code. I dati raccolti sono limitati a:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-slate-400">
            <li>Cognome e Nome</li>
            <li>Grado</li>
            <li>Ente / Amministrazione di appartenenza</li>
            <li>Specializzazione</li>
            <li>Regione e provincia di provenienza</li>
            <li>Numero di cellulare</li>
            <li>Dati di geolocalizzazione (posizione GPS) — <strong className="text-white">solo se</strong> attiva la condivisione o invia un SOS (vedi sezione dedicata)</li>
          </ul>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Titolare del trattamento</h3>
          <p>Dipartimento dei Vigili del Fuoco, del Soccorso Pubblico e della Difesa Civile<br />
          Ministero dell'Interno<br />
          Via del Seminario, 5 – 00186 Roma (RM)</p>
          <p className="mt-2 text-slate-400">Responsabile della Protezione dei Dati (RPD): <span className="text-orange-400">[inserire email istituzionale]</span></p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Finalità del trattamento</h3>
          <p className="mb-2">I dati sono trattati esclusivamente per:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-slate-400">
            <li>Gestire la registrazione e l'identificazione dei soccorritori abilitati all'uso dell'app.</li>
            <li>Costituire ed aggiornare gli elenchi del personale per la pianificazione degli interventi di soccorso.</li>
            <li>Comunicare con il soccorritore per esigenze operative o amministrative (tramite cellulare).</li>
            <li>Localizzare il soccorritore sul campo durante la battuta (se attiva la condivisione) e gestire le richieste di soccorso (SOS) — vedi sezione "Geolocalizzazione e SOS".</li>
          </ul>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Base giuridica</h3>
          <p>Il trattamento è necessario per l'esecuzione di un compito di interesse pubblico o connesso all'esercizio di pubblici poteri di cui è investito il Titolare (<strong className="text-white">art. 6, par. 1, lett. e, GDPR</strong>) – in particolare l'organizzazione del soccorso pubblico.</p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Modalità di trattamento</h3>
          <p>I dati sono trattati con strumenti elettronici, protetti da misure di sicurezza (crittografia, controlli di accesso, autenticazione). L'app funziona in modalità <strong className="text-white">offline-first</strong>: i dati vengono memorizzati localmente e sincronizzati con il server centrale quando la connessione è disponibile.</p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Geolocalizzazione e SOS</h3>
          <p className="mb-2">La condivisione della posizione GPS è <strong className="text-white">facoltativa e disattivata per impostazione predefinita</strong>: si attiva solo con l'apposito interruttore nella pagina e può essere <strong className="text-white">interrotta in qualsiasi momento</strong>. Mentre è attiva, la posizione viene inviata periodicamente alla sala operativa per localizzare il soccorritore sul campo.</p>
          <p className="mb-2">Il pulsante <strong className="text-white">SOS</strong> invia la posizione corrente alla sala operativa <strong className="text-white">anche se la condivisione è disattivata</strong>: premere SOS è un atto volontario di richiesta di soccorso e costituisce consenso all'invio di quella singola posizione (trattamento necessario alla salvaguardia di un interesse vitale, <strong className="text-white">art. 6, par. 1, lett. d, GDPR</strong>).</p>
          <p>I dati di posizione sono <strong className="text-white">effimeri</strong>: conservati solo per la finalità operativa, alleggeriti automaticamente (le tracce più vecchie di alcune ore vengono eliminate) e <strong className="text-white">cancellati alla chiusura dell'operazione</strong>. Non vengono usati per finalità diverse dalla gestione del soccorso.</p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Conservazione</h3>
          <p>I dati anagrafici del soccorritore sono conservati per tutta la durata del rapporto con il Corpo o fino alla richiesta di cancellazione. I dati di posizione GPS sono cancellati alla chiusura dell'operazione (vedi "Geolocalizzazione e SOS").</p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">Diritti dell'interessato</h3>
          <p className="mb-2">Lei ha diritto di:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 text-slate-400">
            <li>Accedere ai propri dati</li>
            <li>Rettificare dati inesatti</li>
            <li>Cancellare i propri dati (quando non più necessari)</li>
            <li>Limitare o opporsi al trattamento</li>
            <li>Proporre reclamo al Garante privacy (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-orange-400 underline hover:text-orange-300">www.garanteprivacy.it</a>)</li>
          </ul>
          <p className="mt-3 text-slate-400">Per esercitare i diritti, contatti il Titolare o il RPD agli indirizzi sopra indicati.</p>
        </section>

        <section>
          <h3 className="font-semibold text-white mb-2">QR code di registrazione</h3>
          <p>L'inquadratura del QR code la reindirizza alla pagina di registrazione, dove deve leggere e accettare la presente informativa prima di inviare i propri dati.</p>
        </section>

        <div className="border-t border-slate-700 pt-4 text-xs text-slate-500">
          Data dell'ultima versione: 24.06.2026
        </div>

      </div>

      <div className="text-center mt-6">
        <button
          className="btn btn-secondary"
          onClick={() => window.history.back()}
        >
          ← Torna alla registrazione
        </button>
      </div>
    </div>
  </div>
);

// ── Intestazione con logo per le pagine pubbliche ─────────────────────────
// Stesso logo/nome configurati in Admin che mostra il login (cache locale subito,
// poi /impostazioni). Lo scudo resta solo come ripiego se non è stato caricato
// alcun logo: prima le pagine di verifica email, reset password e privacy
// mostravano SEMPRE lo scudo, anche con il logo TAS Command configurato.
const IntestazioneTema = ({ piccolo = false, sottotitolo = null }) => {
  const cached = (() => {
    try { return JSON.parse(localStorage.getItem("tas_tema_cache") || "null"); }
    catch { return null; }
  })();
  const [logo, setLogo]   = useState(cached?.logo_base64 || null);
  const [nome, setNome]   = useState(cached?.nome_sistema || "TAS Command");
  const [pronto, setPronto] = useState(!!cached);

  useEffect(() => {
    axios.get(`${API}/impostazioni`).then(({ data }) => {
      if (data.logo_base64) setLogo(data.logo_base64);
      if (data.nome_sistema) setNome(data.nome_sistema);
    }).catch(() => { /* offline: resta cache/ripiego */ }).finally(() => setPronto(true));
  }, []);

  const lato = piccolo ? "h-12" : "h-16";
  return (
    <div className="text-center mb-8">
      {!pronto ? (
        <div className={`${lato} ${piccolo ? "w-12" : "w-16"} mx-auto mb-4`} />
      ) : logo ? (
        <img src={logo} alt="Logo" className={`${lato} w-auto mx-auto mb-4 object-contain`} />
      ) : (
        <div className={`inline-flex items-center justify-center ${piccolo ? "w-12 h-12" : "w-16 h-16"} bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl mb-4`}>
          <Shield size={piccolo ? 24 : 32} className="text-white" />
        </div>
      )}
      <h1 className={`${piccolo ? "text-2xl" : "text-3xl"} font-bold text-white`}>{nome}</h1>
      {sottotitolo && <p className="text-slate-400 text-sm mt-1">{sottotitolo}</p>}
    </div>
  );
};

// ── Verify Email Page ─────────────────────────────────────────────────────
const VerifyEmailPage = () => {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Link di verifica mancante o non valido.");
      return;
    }
    const api = createApi(null);
    api.verifyEmail(token)
      .then(({ data }) => {
        setStatus("success");
        setMessage(data.message || "Email verificata con successo!");
        // Primo accesso: con la verifica email obbligatoria, la registrazione
        // non auto-logga più → il flag va impostato QUI, così al primo login
        // dopo la verifica MainApp porta alla pagina Profilo (una tantum).
        if (!data.already_verified) localStorage.setItem("tas_primo_accesso", "1");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(apiErr(err, "Link non valido o scaduto."));
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md">
        <IntestazioneTema />
        <div className="tactical-card p-8 text-center">
          {status === "loading" && (
            <>
              <div className="mx-auto mb-4 w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-white font-medium">Verifica in corso...</p>
              <p className="text-slate-400 text-sm mt-1">Attendere prego</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle className="mx-auto mb-4 text-green-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Email Verificata!</h2>
              <p className="text-slate-400 mb-6 text-sm">{message}</p>
              <button className="btn btn-primary w-full" onClick={() => window.location.href = "/"}>
                <LogIn size={16} /> Accedi ora
              </button>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="mx-auto mb-4 text-red-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Verifica Fallita</h2>
              <p className="text-slate-400 mb-6 text-sm">{message}</p>
              <button className="btn btn-secondary w-full" onClick={() => window.location.href = "/"}>
                ← Torna al Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ── Reset Password Page (link da email admin) ─────────────────────────────
// Gestisce il percorso /reset-password?token=xxx inviato via email dall'admin.
const ResetPasswordPage = () => {
  const [status, setStatus] = useState("form"); // form | loading | success | error
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [message, setMessage] = useState("");

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  // inFlight: blocca il doppio invio (il token è monouso: il secondo tentativo
  // troverebbe "già utilizzato" e mostrerebbe un errore dopo un cambio riuscito).
  // esitoIncerto: un tentativo è finito senza risposta del server → non sappiamo
  // se la password sia stata cambiata.
  const inFlight = useRef(false);
  const esitoIncerto = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    // Validazione allineata alla policy backend (8+, numero, maiuscola, speciale):
    // se diverge, il backend risponde 422 e la pagina mostrava "token non valido".
    const policyErr = passwordPolicyError(password);
    if (policyErr) {
      toast.error(policyErr);
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    inFlight.current = true;
    setStatus("loading");
    try {
      const api = createApi(null);
      await api.resetPasswordConfirm({ token, new_password: password });
      setStatus("success");
    } catch (err) {
      // Nessuna risposta (rete caduta, timeout) o 5xx: il server può aver GIÀ
      // cambiato la password e a perdersi è stata solo la risposta. Dire "errore,
      // riprova" è falso (e il retry troverebbe il token consumato): si dice la
      // verità e si manda l'utente a provare il login.
      const status = err?.response?.status;
      const ambiguo = !err?.response || status >= 500;
      // Il retry dopo un esito incerto trova il token già usato: stessa situazione.
      const giaUsato = esitoIncerto.current && status === 400;
      if (ambiguo || giaUsato) {
        esitoIncerto.current = true;
        setStatus("incerto");
      } else {
        // Token NON consumato in caso di errore → si resta sul form per riprovare,
        // niente schermata-vicolo-cieco. Lo schermo "error" resta per casi terminali.
        toast.error(apiErr(err, "Errore nel cambio password. Riprova."));
        setStatus("form");
      }
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md">
        <IntestazioneTema />
        <div className="tactical-card p-8">
          {!token ? (
            <div className="text-center">
              <XCircle className="mx-auto mb-4 text-red-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Link non valido</h2>
              <p className="text-slate-400 mb-6 text-sm">Il link di reset non contiene un token valido.</p>
              <button className="btn btn-secondary w-full" onClick={() => window.location.href = "/"}>← Torna al Login</button>
            </div>
          ) : status === "success" ? (
            <div className="text-center">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Password Aggiornata!</h2>
              <p className="text-slate-400 mb-6 text-sm">La tua password è stata cambiata con successo. Puoi ora accedere con le nuove credenziali.</p>
              <button className="btn btn-primary w-full" onClick={() => window.location.href = "/"}>
                <LogIn size={16} /> Accedi ora
              </button>
            </div>
          ) : status === "incerto" ? (
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 text-yellow-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Esito non confermato</h2>
              <p className="text-slate-400 mb-6 text-sm">
                Non abbiamo ricevuto conferma dal server: la password potrebbe essere stata
                cambiata comunque. Prova ad accedere con la nuova password. Se non funziona,
                richiedi un nuovo link con «Password dimenticata».
              </p>
              <button className="btn btn-primary w-full" onClick={() => window.location.href = "/"}>
                <LogIn size={16} /> Vai al login
              </button>
            </div>
          ) : status === "error" ? (
            <div className="text-center">
              <XCircle className="mx-auto mb-4 text-red-500" size={52} />
              <h2 className="text-xl font-bold text-white mb-2">Errore</h2>
              <p className="text-slate-400 mb-6 text-sm">{message}</p>
              <button className="btn btn-secondary w-full" onClick={() => window.location.href = "/"}>← Torna al Login</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 className="text-xl font-bold text-white mb-1">Imposta Nuova Password</h2>
              <p className="text-slate-400 text-sm mb-6">Almeno 8 caratteri, con un numero, una lettera maiuscola e un carattere speciale.</p>
              <div className="form-group">
                <label className="form-label">Nuova Password</label>
                <div className="relative">
                  <input type={showPwd ? "text" : "password"} className="form-input pr-10"
                    value={password} onChange={e => setPassword(e.target.value)}
                    required minLength={8} autoFocus />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    onClick={() => setShowPwd(v => !v)}>
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-group mt-4">
                <label className="form-label">Conferma Password</label>
                <input type={showPwd ? "text" : "password"} className="form-input"
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  required minLength={8} />
              </div>
              <button type="submit" className="btn btn-primary w-full mt-6" disabled={status === "loading"}>
                {status === "loading" ? "Aggiornamento..." : "Conferma Nuova Password"}
              </button>
              <button type="button" className="btn btn-secondary w-full mt-2"
                onClick={() => window.location.href = "/"}>← Annulla</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Theme Utilities ────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
};

const darkenHex = (hex, pct) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = 1 - pct / 100;
  const c = (n) => Math.max(0, Math.min(255, Math.round(n * f))).toString(16).padStart(2, '0');
  return `#${c(rgb.r)}${c(rgb.g)}${c(rgb.b)}`;
};

export const applyTheme = ({ colore_primario, colore_secondario, logo_base64, favicon_base64, nome_sistema } = {}) => {
  const root = document.documentElement;
  if (colore_primario) {
    root.style.setProperty('--color-primary', colore_primario);
    root.style.setProperty('--color-primary-hover', darkenHex(colore_primario, 12));
    const rgb = hexToRgb(colore_primario);
    if (rgb) root.style.setProperty('--color-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }
  if (colore_secondario) {
    root.style.setProperty('--color-secondary', colore_secondario);
    const rgb = hexToRgb(colore_secondario);
    if (rgb) root.style.setProperty('--color-secondary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }
  if (favicon_base64) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = favicon_base64;
  }
  if (nome_sistema) document.title = nome_sistema;
};

// Export components and utils
// Tipi/entità per i filtri dei log (usati da LogsPage e AdminPanel)
const TIPI_LOG = [
  { value: "LOGIN", label: "Login" }, { value: "LOGOUT", label: "Logout" },
  { value: "CREATE", label: "Creazione" }, { value: "UPDATE", label: "Modifica" },
  { value: "DELETE", label: "Eliminazione" }, { value: "ASSIGN", label: "Assegnazione" },
  { value: "STATUS_CHANGE", label: "Cambio stato" }, { value: "VALIDATION", label: "Validazione" },
  { value: "SECURITY", label: "Sicurezza" },
];
const ENTITA_LOG = [
  { value: "OPERAZIONE", label: "Operazione" }, { value: "SOCCORRITORE", label: "Soccorritore" },
  { value: "SQUADRA", label: "Squadra" }, { value: "MISSIONE", label: "Missione" },
  { value: "TURNO", label: "Turno" }, { value: "COMUNICAZIONE", label: "Comunicazione" },
  { value: "OPERATORE", label: "Operatore" }, { value: "SISTEMA", label: "Sistema" },
];

export {
  Modal, LoginPage, AutoRegisterPage, PrivacyPage, VerifyEmailPage, ResetPasswordPage, createApi, generatePDF,
  ENTI, SPECIALIZZAZIONI, TIPI_SQUADRA, TIPI_MISSIONE, STATI_MISSIONE, TIPI_TERRENO,
  TIPI_COMUNICAZIONE, MODALITA_COMUNICAZIONE, PRIORITA_COMUNICAZIONE, STATI_SOCCORRITORE,
  AuthContext, FRONTEND_URL, localNow,
  POA_ENABLED, TIPI_LOG, ENTITA_LOG
};
